import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect, useMemo } from 'react';
import { Plus, Xmark } from 'iconoir-react';
import { C, St, FONT_BODY, FONT_MONO, CARD_STYLE } from '../theme';
import { EMPTY_SET, EMPTY_EX, _globalSettings } from '../constants';
import { rpe2rir, rir2rpe, calcVolume, buildExerciseInsights, progressionComment, filterWarmupSets, calcAvg1RM, calcAvgRPE, trendLabel } from '../science';
import { EXERCISE_DB, findExercise } from '../exerciseDB';
import { parseHeavyNote, parseHevyCSV } from '../parsers';
import { SessionStats } from './Session';
import { ExercisePreview } from './Exercise';
import { ExerciseCharts } from './Charts';
import { sessionFitsScheda, buildColorMap, diffSchedaExercises } from '../utils';
import { FolderIcon } from './Folder';
import { SessionAnalysisPanel } from './SessionAnalysisPanel';
import { getCoachAdvice } from '../pages/CoachChatPage';
import { supabase } from '../supabase';

// ─── COACH DATA ───────────────────────────────────────────────────────────────
export function buildCoachData(session, prevSessions) {
  const allSessions = [session, ...prevSessions];
  const first = prevSessions.length === 0;
  const lines = [];

  for (const ex of session.exercises) {
    const validSets = (ex.sets || []).filter(s => parseFloat(s.weight) > 0 && parseInt(s.reps) > 0);
    if (!validSets.length) continue;

    const prevEx = prevSessions
      .find(s => s.exercises.some(e => e.name.toLowerCase() === ex.name.toLowerCase()))
      ?.exercises.find(e => e.name.toLowerCase() === ex.name.toLowerCase());

    const _intens = _globalSettings.intensitaAttiva === false ? 'off' : (_globalSettings.intensita || 'entrambi');
    const _exEntry = findExercise(ex.name);
    const insights = buildExerciseInsights(ex.sets, ex.type || 'compound', prevEx?.sets || null, ex.name, allSessions, 'auto', _globalSettings.repRange, { showRpe: _intens === 'rpe' || _intens === 'entrambi' });
    // Use progressionComment as fallback to catch confirmed "+Xkg" weight increases
    const primary = insights?.primaryInsight
      || (prevEx ? progressionComment(ex.sets, ex.type || 'compound', prevEx.sets, _globalSettings.repRange, _exEntry?.equipment) : null);
    if (!primary) continue;

    const displayName = _exEntry?.name || ex.name;
    // Use copy if available, otherwise extract first sentence of body
    const hint = primary.copy
      ? primary.copy.split('.')[0]
      : (primary.body || '').split('\n')[0].replace(/\s*\([^)]+\)\s*$/, '').trim();

    if (primary.color === C.green) {
      const m = (primary.copy || primary.body || '').match(/\+[\d.]+\s*kg/);
      lines.push([
        { text: 'Ottimo', bold: true }, { text: ' ' }, { text: displayName, color: C.green },
        m ? { text: `: hai aggiunto ${m[0]} rispetto all'ultima sessione.` }
          : { text: '. Il carico è aumentato rispetto alla sessione precedente.' },
      ]);
    } else if (primary.color === C.blue) {
      lines.push([{ text: 'Mantieni', bold: true }, { text: ' ' }, { text: displayName, color: C.blue }, { text: hint ? `. ${hint}.` : '. Continua con lo stesso carico.' }]);
    } else if (primary.color === C.red) {
      lines.push([{ text: 'Attenzione', bold: true }, { text: ' ' }, { text: displayName, color: C.red }, { text: hint ? `. ${hint}.` : '. Considera di ridurre il carico.' }]);
    } else if (primary.color === C.amber) {
      lines.push([{ text: 'Consolida', bold: true }, { text: ' ' }, { text: displayName, color: C.amber }, { text: hint ? `. ${hint}.` : '. Mantieni il carico attuale.' }]);
    } else if (primary.color === C.purple || primary.color === C.cyan) {
      if (hint) lines.push([{ text: displayName, color: primary.color }, { text: `: ${hint}.` }]);
    }
  }

  if (!first && lines.length === 0) {
    lines.push([{ text: 'Carichi stabili', bold: true }, { text: '. Consolida la tecnica prima di aumentare il carico.' }]);
  }

  return lines;
}

// ─── ANALYZING SCREEN ─────────────────────────────────────────────────────────
function AnalyzingScreen({ session }) {
  const exNames = session.exercises.filter(e => e.name.trim()).map(e => e.name);
  return (
    <div style={{ fontFamily: FONT_BODY, padding: '32px 0 24px', minHeight: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`
        @keyframes gpSweep {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes gpFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .gp-sweep {
          background: linear-gradient(90deg, #e8e8ed 25%, #007AFF22 50%, #e8e8ed 75%);
          background-size: 200% 100%;
          animation: gpSweep 1.4s ease-in-out infinite;
          border-radius: 8px;
        }
        .gp-fadein { animation: gpFadeIn 0.4s ease both; }
      `}</style>

      {/* Pulse icon */}
      <div style={{ width: 56, height: 56, borderRadius: 28, background: `${C.blue}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28 }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M2 12h3l2-7 4 14 3-9 2 5 2-3h4" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Exercise rows sweeping */}
      <div style={{ width: '100%', maxWidth: 280, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {(exNames.length ? exNames : ['Esercizio']).map((name, i) => (
          <div key={i} className="gp-fadein" style={{ animationDelay: `${i * 0.12}s`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="gp-sweep" style={{ width: 6, height: 32, flexShrink: 0, borderRadius: 3 }} />
            <div style={{ flex: 1 }}>
              <div className="gp-sweep" style={{ height: 13, width: '70%', marginBottom: 5 }} />
              <div className="gp-sweep" style={{ height: 10, width: '45%', opacity: 0.6 }} />
            </div>
            <div className="gp-sweep" style={{ width: 44, height: 20, borderRadius: 10 }} />
          </div>
        ))}
      </div>

      <div style={{ fontSize: 14, color: C.subtle, fontWeight: 500, letterSpacing: 0.1 }}>
        Analizzando la sessione...
      </div>
    </div>
  );
}

// kept for consumers that import it
export const MOOD_RATINGS = [
  {emoji:'😔',label:'Pessima', score:1,color:C.red,   bg:C.redL},
  {emoji:'😕',label:'Non bene',score:2,color:C.amber, bg:C.amberL},
  {emoji:'😐',label:'Ok',      score:3,color:'#CA8A04',bg:'#FEF9C3'},
  {emoji:'🙂',label:'Bene',    score:4,color:C.green, bg:C.greenL},
  {emoji:'💪',label:'Ottima',  score:5,color:C.cyan,  bg:C.cyanL},
];
export function EmojiRating() { return null; }

const EX_CARD = {
  background: '#ffffff',
  borderRadius: 12,
  padding: '16px 16px 0',
  marginBottom: 16,
  position: 'relative',
  overflow: 'visible',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  border: '1px solid #EAEAEA',
};

// Uniform gray input used inside the form
const GRAY_IN = {
  width: '100%', border: 'none', borderRadius: 12, padding: '13px 16px',
  fontFamily: FONT_BODY, fontSize: 16, color: '#000',
  outline: 'none', background: '#E8E8ED', boxSizing: 'border-box',
};


// ─── PROGRESSO PANEL ──────────────────────────────────────────────────────────
function ProgressoPanel({ exName, sessions }) {
  const occ = useMemo(() => [...sessions]
    .sort((a, b) => a.date.localeCompare(b.date))
    .flatMap(s => s.exercises
      .filter(e => e.name.toLowerCase() === exName.toLowerCase())
      .map(e => ({ date: s.date, ex: e }))
    ), [exName, sessions]);

  const cd = useMemo(() => occ.map(o => {
    const working = filterWarmupSets(o.ex.sets);
    const vs = working.filter(s => Number(s.reps) > 0);
    const weights = working.map(s => parseFloat(s.weight)).filter(w => w > 0);
    return {
      date: o.date.slice(5),
      rm1: Math.round(calcAvg1RM(o.ex.sets) * 10) / 10,
      volume: Math.round(calcVolume(o.ex.sets)),
      rpe: calcAvgRPE(o.ex.sets) !== null ? Math.round(calcAvgRPE(o.ex.sets) * 10) / 10 : null,
      reps: vs.length ? Math.round(vs.reduce((a, s) => a + Number(s.reps), 0) / vs.length * 10) / 10 : null,
      peso: weights.length ? weights[0] : null,
    };
  }), [occ]);

  const tRM = trendLabel(cd, 'rm1'), tPeso = trendLabel(cd, 'peso'),
    tVol = trendLabel(cd, 'volume'), tReps = trendLabel(cd, 'reps'), tRpe = trendLabel(cd, 'rpe');

  if (occ.length < 2) return (
    <div style={{ fontSize: 13, color: C.subtle, textAlign: 'center', padding: '16px 0' }}>
      Troppo pochi dati per i grafici.
    </div>
  );
  return <ExerciseCharts cd={cd} tRM={tRM} tPeso={tPeso} tVol={tVol} tReps={tReps} tRpe={tRpe} />;
}

// ─── MANUAL FORM ──────────────────────────────────────────────────────────────
const DRAFT_KEY = 'gp_manual_draft';

function fmtLastSession(prevSets, prevSessDate) {
  const working = (prevSets || []).filter(s => parseFloat(s.weight) > 0 && parseInt(s.reps) > 0);
  if (!working.length) return null;
  const groups = [];
  working.forEach(s => {
    const w = parseFloat(s.weight), r = parseInt(s.reps);
    const last = groups[groups.length - 1];
    if (last && last.w === w && last.r === r) last.n++;
    else groups.push({ w, r, n: 1 });
  });
  const setsStr = groups.map(g => g.n > 1 ? `${g.n}x${g.r} a ${g.w}kg` : `${g.r} reps a ${g.w}kg`).join(', ');
  if (!prevSessDate) return setsStr;
  const days = Math.round((Date.now() - new Date(prevSessDate)) / 86400000);
  const when = days === 0 ? 'oggi' : days === 1 ? 'ieri' : `${days} giorni fa`;
  return `${setsStr} · ${when}`;
}

export const ManualForm = forwardRef(function ManualForm({ db, onSave, onClose, onCanSaveChange, onSaved, initialScheda, initialPhase, onStepChange, onStatsChange }, fwdRef) {
  const hasDraft = !initialScheda;
  const draft = hasDraft ? (() => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || null; } catch { return null; } })() : null;

  const [step, setStep] = useState(() => {
    if (initialPhase === 'import') return 0;
    if (initialScheda) return 1;
    if (draft?.scheda) return 1;
    return 0;
  });
  const [date, setDate]   = useState((initialPhase === 'import' ? null : draft?.date) || new Date().toISOString().split('T')[0]);
  const [scheda, setScheda] = useState(initialPhase === 'import' ? '' : (draft?.scheda ?? (initialScheda || '')));
  const [exList, setExList] = useState(() => {
    if (initialPhase === 'import') return [JSON.parse(JSON.stringify(EMPTY_EX))];
    if (draft?.exList?.length) return draft.exList;
    if (!initialScheda) return [JSON.parse(JSON.stringify(EMPTY_EX))];
    const last = [...db.sessions].sort((a, b) => b.date.localeCompare(a.date))
      .find(s => sessionFitsScheda(s.workoutName, initialScheda));
    if (last?.exercises?.length)
      return last.exercises.map(ex => ({ ...ex, sets: ex.sets.map(() => ({ weight:'', reps:'', rpe:'', rir:'' })) }));
    const obj = (db.schede || []).find(s => s.name === initialScheda);
    if (obj?.exercises?.length)
      return obj.exercises.map(ex => ({ name: ex.name, note: '', sets: Array.from({ length: ex.sets || 3 }, () => ({ ...EMPTY_SET })) }));
    return [JSON.parse(JSON.stringify(EMPTY_EX))];
  });
  const [duration, setDuration] = useState(draft?.duration || '');
  const [saveErr, setSaveErr]   = useState('');
  const [analyzing, setAnalyzing]   = useState(false);
  const [savedSession, setSavedSession] = useState(null);
  const [prNames, setPrNames] = useState([]);
  const [exSearch, setExSearch] = useState({});
  const [, setExFocus]  = useState(null);
  const [showHevyImport, setShowHevyImport] = useState(false);
  const [hevyRaw, setHevyRaw] = useState('');
  const [hevyErr, setHevyErr] = useState('');
  const [showTextImport, setShowTextImport] = useState(false);
  const [textRaw, setTextRaw] = useState('');
  const [textErr, setTextErr] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [exMenuOpen, setExMenuOpen] = useState(null);   // ei index of open card menu
  const [replacingIdx, setReplacingIdx] = useState(null); // ei index for replace-exercise search
  const [setFeedback, setSetFeedback] = useState({}); // {ei: {chipText, chipColor, deltaText, deltaColor}}
  const [step0Phase, setStep0Phase] = useState(initialPhase || 'list'); // 'list' | 'import' | 'manual-picker'
  const [schedaDiffPending, setSchedaDiffPending] = useState(null); // { parsed, schedaObj, added, removed, triggerSave, fromSave }
  const [isImportMode, setIsImportMode] = useState(false); // true when form is filled from Hevy/text import (post-session)
  const [isManualEntry, setIsManualEntry] = useState(false); // true when form is opened via "Inserimento manuale"
  // Sync quando initialPhase cambia dopo il mount (es. launcher apre direttamente import)
  const prevInitPhaseRef = useRef(initialPhase);
  useEffect(() => {
    if (initialPhase && initialPhase !== prevInitPhaseRef.current) {
      setStep0Phase(initialPhase);
      if (initialPhase === 'import') {
        setStep(0);
        setScheda('');
        setExList([JSON.parse(JSON.stringify(EMPTY_EX))]);
        setDate(new Date().toISOString().split('T')[0]);
      }
    }
    prevInitPhaseRef.current = initialPhase;
  }, [initialPhase]);
  const [currentExIdx, setCurrentExIdx] = useState(0); // stepper: which exercise is shown
  const [showProgresso, setShowProgresso] = useState(false);
  const [activeExTab, setActiveExTab] = useState(null); // 'grafici' | 'analisi' | 'confronto' | null
  const [doneSets, setDoneSets] = useState({}); // {ei: Set<si>} — set marcati come completati
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [restTimerDuration, setRestTimerDuration] = useState(90);
  const [restTimerState, setRestTimerState] = useState({ running: false, remaining: 0, intervalId: null });
  const restTimerCleanup = useRef(null);
  const pendingImportSave = useRef(false);
  const scrollBodyRef = useRef(null);
  const sessionStartRef = useRef(Date.now());
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsedSec(Math.round((Date.now() - sessionStartRef.current) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const computeSetFeedback = (ei, updatedSets, lastTouchedSi) => {
    const ex = exList[ei];
    if (!ex?.name) return;
    const sessionSets = updatedSets.slice(0, lastTouchedSi + 1)
      .filter(s => parseFloat(s.weight) > 0 && parseInt(s.reps) > 0);
    if (!sessionSets.length) {
      setSetFeedback(f => { const n = {...f}; delete n[ei]; return n; });
      return;
    }
    const prevSess = [...db.sessions].sort((a,b) => b.date.localeCompare(a.date))
      .find(s => s.exercises.some(e => e.name.toLowerCase() === ex.name.toLowerCase()));
    const prevSets = prevSess?.exercises.find(e => e.name.toLowerCase() === ex.name.toLowerCase())?.sets || null;
    const prevValid = prevSets ? prevSets.filter(s => parseFloat(s.weight) > 0 && parseInt(s.reps) > 0) : [];

    // No history — show neutral "prima volta"
    if (!prevValid.length) {
      setSetFeedback(f => ({ ...f, [ei]: { chipText: 'Prima volta', chipColor: C.cyan, deltaText: null, deltaColor: C.subtle } }));
      return;
    }

    const curS1reps = parseInt(sessionSets[0].reps);
    const curS1weight = parseFloat(sessionSets[0].weight);
    const prevS1reps = parseInt(prevValid[0].reps);
    const prevS1weight = parseFloat(prevValid[0].weight);
    const repDelta = curS1reps - prevS1reps;
    const weightDelta = parseFloat((curS1weight - prevS1weight).toFixed(2));
    const repDeltaPct = prevS1reps > 0 ? (repDelta / prevS1reps) * 100 : 0;

    // Delta line: show weight first if it changed, else reps
    const repPart = repDelta > 0 ? `+${repDelta} reps` : repDelta < 0 ? `${repDelta} reps` : null;
    const wPart = weightDelta > 0 ? `+${weightDelta}kg` : weightDelta < 0 ? `${weightDelta}kg` : null;
    const parts = [wPart, repPart].filter(Boolean);
    const deltaText = parts.length ? `${parts.join(', ')} vs ultima (${prevS1weight}kg x ${prevS1reps})` : `Uguale all'ultima (${prevS1weight}kg x ${prevS1reps})`;
    const deltaColor = (repDelta > 0 || weightDelta > 0) ? C.green : (repDelta < 0 || weightDelta < 0) ? C.amber : C.subtle;

    // Status chip
    let chipText, chipColor;
    if (repDeltaPct >= 50 && weightDelta >= 0) {
      chipText = 'Record personale'; chipColor = C.green;
    } else if (weightDelta > 0) {
      chipText = `+${weightDelta}kg`; chipColor = C.green;
    } else if (repDelta > 0 && weightDelta === 0) {
      chipText = `+${repDelta} reps`; chipColor = C.green;
    } else if (repDelta === 0 && weightDelta === 0) {
      chipText = 'Stabile'; chipColor = C.blue;
    } else if (repDelta < 0 && weightDelta === 0) {
      chipText = `${repDelta} reps`; chipColor = C.amber;
    } else if (weightDelta < 0) {
      chipText = `${weightDelta}kg`; chipColor = C.amber;
    } else {
      chipText = null; chipColor = C.blue;
    }

    setSetFeedback(f => ({ ...f, [ei]: { chipText, chipColor, deltaText, deltaColor } }));
  };

  // ── chat: volume feedback bubble after each set log ──────────────────────

  // ── draft persistence ────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasDraft) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ date, scheda, exList, duration, step })); } catch {}
  }, [date, scheda, exList, duration, step, hasDraft]);

  // ── when on step 0, signal parent that form is not saveable ─────────────
  useEffect(() => {
    if (step === 0) onCanSaveChange?.(false);
    onStepChange?.(step);
  }, [step, onCanSaveChange, onStepChange]);

  // cleanup rest timer on unmount
  useEffect(() => {
    return () => {
      if (restTimerCleanup.current) clearInterval(restTimerCleanup.current);
    };
  }, []);

  // trigger save after async import sets exList
  useEffect(() => {
    if (pendingImportSave.current) {
      pendingImportSave.current = false;
      save();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exList]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch {} };

  const startRestTimer = () => {
    if (restTimerCleanup.current) clearInterval(restTimerCleanup.current);
    const id = setInterval(() => {
      setRestTimerState(prev => {
        if (prev.remaining <= 1) {
          clearInterval(prev.intervalId);
          restTimerCleanup.current = null;
          return { running: false, remaining: 0, intervalId: null };
        }
        return { ...prev, remaining: prev.remaining - 1 };
      });
    }, 1000);
    restTimerCleanup.current = id;
    setRestTimerState({ running: true, remaining: restTimerDuration, intervalId: id });
  };

  const stopRestTimer = () => {
    if (restTimerCleanup.current) clearInterval(restTimerCleanup.current);
    restTimerCleanup.current = null;
    setRestTimerState({ running: false, remaining: 0, intervalId: null });
  };

  // ── diff check helper ────────────────────────────────────────────────────
  // Commit imported exercises to exList (and optionally update scheda template).
  const commitImport = (parsed, triggerSave = false) => {
    setExList(parsed.exercises.map(ex => ({ ...ex, sets: ex.sets.map(s => ({ ...s })) })));
    if (parsed.date) setDate(parsed.date);
    if (parsed.workoutName) setScheda(parsed.workoutName);
    if (triggerSave) { setIsImportMode(true); pendingImportSave.current = true; }
    setTimeout(() => scrollBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const checkDiffThenCommit = (parsed, triggerSave = false) => {
    const currentScheda = scheda || parsed.workoutName || '';
    const schedaObj = (db.schede || []).find(s => s.name === currentScheda);
    if (schedaObj?.exercises?.length) {
      const importedNames = parsed.exercises.map(e => e.name);
      const diff = diffSchedaExercises(importedNames, schedaObj.exercises);
      if (diff.hasChanges) {
        setSchedaDiffPending({ parsed, schedaObj, added: diff.added, removed: diff.removed, triggerSave, fromSave: false });
        return;
      }
    }
    commitImport(parsed, triggerSave);
  };

  // ── hevy import ─────────────────────────────────────────────────────────
  const applyHevyImport = (triggerSave = false) => {
    setHevyErr('');
    const parsed = parseHeavyNote(hevyRaw);
    if (!parsed) { setHevyErr('Formato non riconosciuto. Incolla il testo copiato da Hevy.'); return; }
    setHevyRaw('');
    setShowHevyImport(false);
    checkDiffThenCommit(parsed, triggerSave);
  };

  const applyTextImport = async (triggerSave = false) => {
    if (!textRaw.trim()) return;
    setTextLoading(true);
    setTextErr('');
    if (textRaw.length > 2000) {
      setTextErr('Testo troppo lungo. Massimo 2000 caratteri.');
      setTextLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('hyper-handler', {
        body: { text: textRaw },
      });
      if (error) { setTextErr('Servizio non disponibile. Riprova tra qualche minuto.'); return; }
      if (!data?.exercises?.length) { setTextErr('Nessun esercizio riconosciuto. Prova a descrivere in modo piu dettagliato.'); return; }
      const parsedText = {
        date: null,
        workoutName: scheda || '',
        exercises: data.exercises.map(ex => ({
          name: (findExercise(ex.name)?.name) || ex.name || '',
          sets: (ex.sets || [{ weight:'', reps:'', rpe:'', rir:'' }]).map(s => ({
            weight: s.weight != null ? String(s.weight) : '',
            reps:   s.reps  != null ? String(s.reps)  : '',
            rpe:    s.rpe   != null ? String(s.rpe)   : '',
            rir:    s.rir   != null ? String(s.rir)   : '',
          })),
        })),
      };
      setTextRaw('');
      setShowTextImport(false);
      checkDiffThenCommit(parsedText, triggerSave);
    } catch {
      setTextErr('Errore imprevisto. Riprova.');
    } finally {
      setTextLoading(false);
    }
  };

  // ── exercise helpers ────────────────────────────────────────────────────
  const updEx  = (ei,f,v) => setExList(l => l.map((e,i) => i!==ei ? e : {...e,[f]:v}));
  const updSet = (ei,si,f,v) => setExList(l => l.map((e,i) => {
    if(i!==ei) return e;
    const sets = e.sets.map((s,j) => {
      if(j!==si) return s;
      const ns={...s,[f]:v};
      if(f==='rpe') ns.rir=String(rpe2rir(v));
      if(f==='rir') ns.rpe=String(rir2rpe(v));
      return ns;
    });
    if (f === 'weight' || f === 'reps') {
      computeSetFeedback(ei, sets, si);
    }
    return {...e,sets};
  }));
  const addSet = ei => setExList(l => l.map((e,i) => i!==ei?e:{...e,sets:[...e.sets,{...EMPTY_SET}]}));
  const addEx  = () => setExList(l => [...l, JSON.parse(JSON.stringify(EMPTY_EX))]);
  const remEx  = ei => setExList(l => l.filter((_,i) => i!==ei));

  // ── save ────────────────────────────────────────────────────────────────
  const save = (skipDiffCheck = false) => {
    setSaveErr('');
    const validEx = exList
      .filter(e => e.name.trim() && e.sets.some(s => parseFloat(s.weight)>0 && parseInt(s.reps)>0))
      .map(e => { const dbEx=findExercise(e.name); return {...e,primaryMuscle:dbEx?.primaryMuscle||null,muscles:dbEx?.muscles||[]}; });
    if(!validEx.length){ setSaveErr('Inserisci almeno un esercizio con kg e reps.'); return; }
    if (!skipDiffCheck && scheda) {
      const schedaObj = (db.schede || []).find(s => s.name === scheda);
      if (schedaObj?.exercises?.length) {
        const importedNames = validEx.map(e => e.name);
        const diff = diffSchedaExercises(importedNames, schedaObj.exercises);
        if (diff.hasChanges) {
          const fakeParsed = { date, workoutName: scheda, exercises: validEx };
          setSchedaDiffPending({ parsed: fakeParsed, schedaObj, added: diff.added, removed: diff.removed, triggerSave: false, fromSave: true });
          return;
        }
      }
    }
    const autoDuration = elapsedSec >= 60 ? Math.round(elapsedSec / 60) : (duration ? parseInt(duration) : null);
    const session = {
      id: Date.now(), date, workoutName: scheda||'',
      exercises: validEx, wellness: null,
      duration: autoDuration,
    };
    // Raccoglie esercizi con PR rilevati durante il logging
    const detectedPRs = validEx
      .map((ex, i) => {
        const ei = exList.indexOf(exList.find(e => e.name === ex.name));
        return setFeedback[ei]?.chipText === 'Record personale' ? ex.name : null;
      })
      .filter(Boolean);
    setPrNames(detectedPRs);
    clearDraft();
    setAnalyzing(true);
    onSaved?.();
    setTimeout(() => {
      setSavedSession(session);
      setAnalyzing(false);
    }, 2000);
  };

  // notify parent of save-readiness
  const canSave = exList.some(e => e.name.trim() && e.sets.some(s => parseFloat(s.weight)>0 && parseInt(s.reps)>0));
  useEffect(() => { onCanSaveChange?.(canSave); }, [canSave, onCanSaveChange]);
  const latestSave = useRef(save);
  latestSave.current = save;
  // notify parent of stats (name + exercise/set count)
  useEffect(() => {
    const exCount = exList.filter(e => e.name.trim()).length;
    const totalSets = exList.reduce((s, ex) => s + ex.sets.length, 0);
    onStatsChange?.({ exCount, totalSets, schedaName: scheda });
  }, [exList, scheda, onStatsChange]);

  useImperativeHandle(fwdRef, () => ({
    save: () => latestSave.current(),
    goBack: () => { if (step === 1 && !initialScheda) { setStep(0); setStep0Phase('list'); onCanSaveChange?.(false); } },
    getStep: () => step,
    addEx: () => addEx(),
    openHevyImport: () => { setShowHevyImport(true); setHevyErr(''); },
    openTextImport: () => { setShowTextImport(true); setTextErr(''); setTextRaw(''); },
  }));

  // ── analyzing ────────────────────────────────────────────────────────────
  if (analyzing) {
    return <AnalyzingScreen session={{ exercises: exList.filter(e => e.name.trim()) }} />;
  }

  // ── post-save session review ─────────────────────────────────────────────
  if (savedSession) {
    const prevSessions = [...db.sessions].sort((a,b)=>b.date.localeCompare(a.date));
    const schedaColor = buildColorMap(db.schede||[], db.sessions)[savedSession.workoutName] || C.blue;
    const totalVol = Math.round(savedSession.exercises.reduce((s,e)=>s+calcVolume(e.sets||[]),0));
    const totalSets = savedSession.exercises.reduce((s,e)=>s+(e.sets||[]).filter(x=>parseFloat(x.weight)>0&&parseInt(x.reps)>0).length,0);
    const totalReps = savedSession.exercises.reduce((s,e)=>s+(e.sets||[]).reduce((a,x)=>a+(parseInt(x.reps)||0),0),0);
    const prevSessForDelta = prevSessions.find(s=>savedSession.exercises.some(e=>s.exercises?.some(pe=>pe.name.toLowerCase()===e.name.toLowerCase())));
    const prevTotalVol = prevSessForDelta ? Math.round(prevSessForDelta.exercises.reduce((s,e)=>s+calcVolume(e.sets||[]),0)) : null;
    const volDelta = prevTotalVol && prevTotalVol>0 ? ((totalVol-prevTotalVol)/prevTotalVol*100) : null;
    const prevTotalSets = prevSessForDelta ? prevSessForDelta.exercises.reduce((s,e)=>s+(e.sets||[]).filter(x=>parseFloat(x.weight)>0&&parseInt(x.reps)>0).length,0) : null;
    const setsDelta = prevTotalSets!==null ? totalSets-prevTotalSets : null;
    const pesoMax = Math.max(0,...savedSession.exercises.flatMap(e=>(e.sets||[]).map(s=>parseFloat(s.weight)||0)));
    const prevPesoMax = prevSessForDelta ? Math.max(0,...prevSessForDelta.exercises.flatMap(e=>(e.sets||[]).map(s=>parseFloat(s.weight)||0))) : null;
    const pesoDelta = prevPesoMax!==null && prevPesoMax>0 ? pesoMax-prevPesoMax : null;

    return (
      <div style={{fontFamily:FONT_BODY,height:'100%',overflowY:'auto',WebkitOverflowScrolling:'touch'}}>
        {/* Close button — left, chiede conferma prima di scartare */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'flex-start',marginBottom:4}}>
          <button onClick={()=>setConfirmDiscard(true)}
            style={{background:'rgba(120,120,128,0.12)',border:'none',borderRadius:999,
              width:32,height:32,cursor:'pointer',display:'flex',alignItems:'center',
              justifyContent:'center',color:C.muted}}>
            <Xmark width={16} height={16}/>
          </button>
        </div>
        {/* Header — FolderIcon + contained animated blob */}
        <div style={{textAlign:'center',padding:'8px 0 20px'}}>
          <style>{`
            @keyframes gpBlob {
              0%,100% { transform: scale(1); }
              33%      { transform: scale(1.1) translate(5%,-4%); }
              66%      { transform: scale(0.95) translate(-4%,5%); }
            }
          `}</style>
          <div style={{position:'relative',display:'inline-flex',alignItems:'center',justifyContent:'center',width:150,height:150,marginBottom:14}}>
            <div style={{
              position:'absolute',inset:0,borderRadius:'50%',
              background:`radial-gradient(circle, ${schedaColor}40 0%, transparent 70%)`,
              animation:'gpBlob 4s ease-in-out infinite',
              pointerEvents:'none',
            }}/>
            <div style={{position:'relative'}}>
              <FolderIcon accentColor={schedaColor} size={108} isActive={true}/>
            </div>
          </div>
          <div style={{fontSize:22,fontWeight:700,color:C.text,letterSpacing:-0.5,lineHeight:1.1}}>
            {savedSession.workoutName||'Allenamento'}
          </div>
          <div style={{fontSize:14,color:C.subtle,marginTop:5,textTransform:'capitalize'}}>
            {new Date(savedSession.date).toLocaleDateString('it-IT',{weekday:'long',day:'numeric',month:'long'})}
          </div>
        </div>

        {/* PR celebration */}
        {prNames.length > 0 && (
          <div style={{
            background:'#CCFF00',borderRadius:14,padding:'14px 16px',marginBottom:16,
            display:'flex',alignItems:'center',gap:12,
            animation:'gpFadeIn 0.4s ease both',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"
                fill="#000" opacity="0.85"/>
            </svg>
            <div>
              <div style={{fontSize:14,fontWeight:800,color:'#000',fontFamily:FONT_BODY,lineHeight:1.2}}>
                Nuovo record
              </div>
              <div style={{fontSize:13,color:'rgba(0,0,0,0.7)',fontFamily:FONT_BODY,marginTop:2}}>
                {prNames.join(', ')}
              </div>
            </div>
          </div>
        )}

        {/* Riepilogo sessione */}
        <div style={{padding:'0 4px 20px',borderBottom:'1px solid rgba(60,60,67,0.10)',marginBottom:20,overflowX:'hidden',wordBreak:'break-word'}}>
          {/* Pill label */}
          <div style={{marginBottom:14}}>
            <span style={{display:'inline-flex',alignItems:'center',padding:'5px 14px',borderRadius:99,background:`${schedaColor}12`,color:schedaColor,fontWeight:600,fontSize:13,fontFamily:FONT_BODY}}>
              Riepilogo sessione
            </span>
          </div>
          {/* Volume hero */}
          <div style={{display:'flex',alignItems:'baseline',gap:6,marginBottom:4,flexWrap:'wrap'}}>
            <span style={{fontSize:38,fontWeight:800,color:C.text,letterSpacing:-1.5,lineHeight:1,fontFamily:FONT_BODY}}>{totalVol.toLocaleString('it-IT')}</span>
            <span style={{fontSize:15,color:C.subtle,fontFamily:FONT_BODY}}>kg·reps</span>
            {volDelta!==null&&(
              <span style={{...St.tag(volDelta===0?C.subtle:volDelta>0?C.green:C.red),fontSize:13}}>
                {volDelta>0?'+':''}{volDelta.toFixed(1)}%
              </span>
            )}
          </div>
          <div style={{fontSize:13,color:C.subtle,marginBottom:16,fontFamily:FONT_BODY}}>{totalSets} serie · {totalReps} reps · {savedSession.exercises.length} esercizi</div>
          {/* Breakdown comparativo vs sessione precedente */}
          {prevSessForDelta && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:4}}>
              {[
                { label:'Volume', value:`${totalVol.toLocaleString('it-IT')} kg`, delta: volDelta!==null ? `${volDelta>0?'+':''}${volDelta.toFixed(0)}%` : null, pos: volDelta!==null ? volDelta>=0 : null },
                { label:'Serie', value:`${totalSets}`, delta: setsDelta!==null ? `${setsDelta>0?'+':''}${setsDelta}` : null, pos: setsDelta!==null ? setsDelta>=0 : null },
                { label:'Peso max', value:`${pesoMax}kg`, delta: pesoDelta!==null ? `${pesoDelta>0?'+':''}${pesoDelta}kg` : null, pos: pesoDelta!==null ? pesoDelta>=0 : null },
              ].map(({label,value,delta,pos})=>(
                <div key={label} style={{background:'rgba(120,120,128,0.07)',borderRadius:12,padding:'10px 10px 8px',textAlign:'center'}}>
                  <div style={{fontSize:11,color:C.subtle,fontWeight:500,marginBottom:4,textTransform:'uppercase',letterSpacing:0.5}}>{label}</div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text,lineHeight:1.1}}>{value}</div>
                  {delta && <div style={{fontSize:12,fontWeight:600,color:pos?C.green:C.red,marginTop:3}}>{delta}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {savedSession.exercises.map((ex,i)=>{
          const prev = prevSessions
            .find(s=>s.exercises.some(e=>e.name.toLowerCase().trim()===ex.name.toLowerCase().trim()))
            ?.exercises.find(e=>e.name.toLowerCase().trim()===ex.name.toLowerCase().trim());
          return(
            <ExercisePreview key={i} ex={ex} prevEx={prev}
              allSessions={[savedSession,...db.sessions]} showInsights={false}
              onSave={updatedEx=>setSavedSession(prev=>({...prev,exercises:prev.exercises.map((e,j)=>j===i?updatedEx:e)}))}
              onDelete={()=>setSavedSession(prev=>({...prev,exercises:prev.exercises.filter((_,j)=>j!==i)}))}/>
          );
        })}

        <button onClick={() => { onSave({...db, sessions:[savedSession,...db.sessions]}); onClose(); }}
          style={{...St.btn(C.blue),width:'100%',padding:'15px',borderRadius:14,fontSize:16,marginTop:12,fontFamily:FONT_BODY}}>
          Salva sessione
        </button>
        <button onClick={()=>setConfirmDiscard(true)}
          style={{width:'100%',padding:'13px',borderRadius:14,fontSize:15,marginTop:8,
            background:'none',border:'none',cursor:'pointer',color:C.subtle,fontFamily:FONT_BODY}}>
          Scarta sessione
        </button>

        {confirmDiscard&&(
          <div style={{position:'fixed',inset:0,zIndex:500,background:'rgba(0,0,0,0.40)',display:'flex',alignItems:'flex-end',justifyContent:'center'}}
            onClick={e=>{if(e.target===e.currentTarget)setConfirmDiscard(false);}}>
            <div style={{width:'100%',maxWidth:560,background:'#fff',borderRadius:'16px 16px 0 0',
              paddingBottom:'max(28px,env(safe-area-inset-bottom,16px))'}}>
              <div style={{width:36,height:4,borderRadius:2,background:'rgba(120,120,128,0.28)',margin:'12px auto 16px'}}/>
              <div style={{padding:'0 20px 20px',textAlign:'center'}}>
                <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:8,fontFamily:FONT_BODY}}>Scartare la sessione?</div>
                <div style={{fontSize:14,color:C.subtle,fontFamily:FONT_BODY,lineHeight:1.5}}>I dati inseriti non saranno salvati.</div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:8,padding:'0 16px'}}>
                <button onClick={onClose}
                  style={{width:'100%',padding:'15px',borderRadius:14,fontSize:16,fontWeight:700,
                    background:C.red,color:'#fff',border:'none',cursor:'pointer',fontFamily:FONT_BODY}}>
                  Scarta
                </button>
                <button onClick={()=>setConfirmDiscard(false)}
                  style={{width:'100%',padding:'13px',borderRadius:14,fontSize:15,
                    background:'none',border:'none',cursor:'pointer',color:C.blue,fontFamily:FONT_BODY}}>
                  Continua a modificare
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── scheda diff bottom sheet ─────────────────────────────────────────────
  if (schedaDiffPending) {
    const { parsed, schedaObj, added, removed, triggerSave, fromSave } = schedaDiffPending;

    const applyAndProceed = (updateScheda) => {
      if (updateScheda) {
        const newExercises = parsed.exercises.map(e => {
          const old = schedaObj.exercises.find(o => o.name.toLowerCase() === e.name.toLowerCase());
          return { name: e.name, sets: old?.sets || 3, type: e.type || old?.type || 'compound', primaryMuscle: e.primaryMuscle || old?.primaryMuscle || null };
        });
        const updatedSchede = (db.schede || []).map(s => s.id === schedaObj.id ? { ...s, exercises: newExercises } : s);
        onSave({ ...db, schede: updatedSchede });
      }
      setSchedaDiffPending(null);
      if (fromSave) {
        save(true);
      } else {
        commitImport(parsed, triggerSave);
        setStep(1);
      }
    };

    return (
      <div style={{ fontFamily: FONT_BODY }}>
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.28)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 560, background: '#fff', borderRadius: '20px 20px 0 0', padding: `24px 24px max(28px,env(safe-area-inset-bottom,20px))` }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(120,120,128,0.28)', margin: '0 auto 20px' }} />
            <div style={{ fontSize: 19, fontWeight: 700, color: C.text, marginBottom: 6 }}>Esercizi cambiati</div>
            <div style={{ fontSize: 14, color: C.subtle, marginBottom: 20, lineHeight: 1.5 }}>
              Hai allenato esercizi diversi dalla scheda <span style={{ fontWeight: 600, color: C.text }}>{schedaObj.name}</span>:
            </div>
            <div style={{ background: '#F2F2F7', borderRadius: 14, padding: '14px 16px', marginBottom: 24 }}>
              {added.map(name => (
                <div key={`+${name}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                  <div style={{ width: 20, height: 20, borderRadius: 10, background: C.greenL, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 2v6M2 5h6" stroke={C.green} strokeWidth="1.8" strokeLinecap="round"/></svg>
                  </div>
                  <span style={{ fontSize: 15, color: C.text, fontWeight: 500 }}>{name}</span>
                </div>
              ))}
              {removed.map(name => (
                <div key={`-${name}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                  <div style={{ width: 20, height: 20, borderRadius: 10, background: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5h6" stroke={C.subtle} strokeWidth="1.8" strokeLinecap="round"/></svg>
                  </div>
                  <span style={{ fontSize: 15, color: C.subtle }}>{name}</span>
                </div>
              ))}
            </div>
            <button onClick={() => applyAndProceed(true)}
              style={{ ...St.btn(C.blue), width: '100%', padding: '14px', borderRadius: 14, fontSize: 16, marginBottom: 10, fontFamily: FONT_BODY }}>
              Aggiorna scheda
            </button>
            <button onClick={() => applyAndProceed(false)}
              style={{ width: '100%', padding: '14px', borderRadius: 14, fontSize: 15, background: 'none', border: 'none', cursor: 'pointer', color: C.subtle, fontFamily: FONT_BODY, fontWeight: 500 }}>
              Mantieni scheda invariata
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── step 0: scheda selection + import ───────────────────────────────────
  if (step === 0) {
    const activeSchede = (db.schede || []).filter(s => s.active !== false);
    const colorMap = buildColorMap(db.schede || [], db.sessions);
    const pickScheda = (name) => {
      setScheda(name);
      if (name) {
        const last = [...db.sessions].sort((a, b) => b.date.localeCompare(a.date))
          .find(s => sessionFitsScheda(s.workoutName, name));
        if (last?.exercises?.length) {
          setExList(last.exercises.map(ex => ({ ...ex, sets: ex.sets.map(s => ({ ...s })) })));
        } else {
          const obj = (db.schede || []).find(s => s.name === name);
          if (obj?.exercises?.length)
            setExList(obj.exercises.map(ex => ({ name: ex.name, note: '', sets: Array.from({ length: ex.sets || 3 }, () => ({ ...EMPTY_SET })) })));
          else
            setExList([JSON.parse(JSON.stringify(EMPTY_EX))]);
        }
      } else {
        setExList([JSON.parse(JSON.stringify(EMPTY_EX))]);
      }
      setCurrentExIdx(0);
      setStep0Phase('import');
    };
    const CHEVRON = (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <path d="M9 6l6 6-6 6" stroke={C.subtle} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
    const allRows = [
      ...activeSchede.map(s => ({ name: s.name, dot: colorMap[s.name] || C.blue, empty: false })),
      { name: 'Senza scheda', dot: null, empty: true },
    ];
    if (step0Phase === 'manual-picker') {
      const manualRows = [
        ...activeSchede.map(s => ({ name: s.name, dot: colorMap[s.name] || C.blue, empty: false })),
        { name: 'Senza scheda', dot: null, empty: true },
      ];
      return (
        <div style={{ fontFamily: FONT_BODY, padding: '0 0 8px' }}>
          <button onClick={()=> setStep0Phase('import')}
            style={{background:'none',border:'none',cursor:'pointer',padding:'0 0 16px',
              fontSize:15,color:C.blue,fontFamily:FONT_BODY,display:'flex',alignItems:'center',gap:6}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 6l-6 6 6 6" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Indietro
          </button>
          <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:4}}>Inserimento manuale</div>
          <div style={{fontSize:13,color:C.subtle,marginBottom:20}}>Scegli la scheda da compilare</div>
          <div style={{ ...CARD_STYLE, overflow: 'hidden', padding: 0, background: '#F2F2F7', boxShadow: 'none', border: 'none' }}>
            {manualRows.map((row, i) => (
              <div key={row.name} style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    pickScheda(row.empty ? '' : row.name);
                    setIsManualEntry(true);
                    setStep(1);
                  }}
                  onPointerDown={e => { e.currentTarget.style.background = 'rgba(120,120,128,0.08)'; }}
                  onPointerUp={e => { e.currentTarget.style.background = 'none'; }}
                  onPointerCancel={e => { e.currentTarget.style.background = 'none'; }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '14px 16px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: FONT_BODY, textAlign: 'left',
                    WebkitTapHighlightColor: 'transparent',
                    transition: 'background 0.1s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {row.empty
                      ? <div style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${C.border}`, flexShrink: 0 }} />
                      : <div style={{ width: 12, height: 12, borderRadius: '50%', background: row.dot, flexShrink: 0 }} />
                    }
                    <span style={{ fontSize: 16, color: row.empty ? C.subtle : C.text, fontWeight: row.empty ? 400 : 500 }}>
                      {row.name}
                    </span>
                  </div>
                  {CHEVRON}
                </button>
                {i < manualRows.length - 1 && (
                  <div style={{ height: '0.5px', background: 'rgba(60,60,67,0.18)', marginLeft: 40 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (step0Phase === 'import') {
      return (
        <div style={{ fontFamily: FONT_BODY, padding: '0 0 8px' }}>
          {showHevyImport && (
            <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'flex-end',justifyContent:'center'}}
              onClick={e=>{if(e.target===e.currentTarget){setShowHevyImport(false);setHevyRaw('');setHevyErr('');}}}>
              <div style={{width:'100%',maxWidth:560,background:'#fff',borderRadius:'16px 16px 0 0',
                padding:'20px 20px max(24px,env(safe-area-inset-bottom,16px))'}}>
                <div style={{width:36,height:4,borderRadius:2,background:'rgba(120,120,128,0.28)',margin:'0 auto 16px'}}/>
                <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:4}}>Importa da Hevy</div>
                <div style={{fontSize:13,color:C.subtle,marginBottom:12,lineHeight:1.5}}>
                  Copia il testo della sessione da Hevy (condividi sessione, copia testo) e incollalo qui.
                </div>
                <div style={{background:'#F2F2F7',borderRadius:10,padding:'10px 12px',marginBottom:10,
                  fontSize:12,color:C.subtle,fontFamily:FONT_MONO,lineHeight:1.7,border:`1px dashed ${C.border}`}}>
                  Dorso e tricipiti<br/>Il giorno venerdì, mag 15, 2026 alle ore 5:01pm<br/>Lat Pulldown (Cavo)<br/>Serie 1: 50 kg x 8 @ 9.5 rpe
                </div>
                <textarea value={hevyRaw} onChange={e=>{setHevyRaw(e.target.value);setHevyErr('');}}
                  placeholder="Incolla qui le note da Hevy..."
                  rows={7}
                  style={{width:'100%',border:'none',borderRadius:12,padding:'12px 14px',
                    fontFamily:FONT_MONO,fontSize:13,color:C.text,outline:'none',
                    background:'#F2F2F7',boxSizing:'border-box',resize:'vertical',lineHeight:1.6}}/>
                {hevyErr && (
                  <div style={{fontSize:13,color:C.red,padding:'8px 12px',background:C.redL,
                    borderRadius:10,marginTop:8,lineHeight:1.5}}>{hevyErr}</div>
                )}
                <button onClick={()=>applyHevyImport(true)}
                  disabled={!hevyRaw.trim()}
                  style={{...St.btn(C.blue),width:'100%',padding:'13px',borderRadius:13,
                    fontSize:15,marginTop:12,fontFamily:FONT_BODY,
                    opacity:hevyRaw.trim()?1:0.45}}>
                  Importa sessione
                </button>
                <button onClick={()=>{setShowHevyImport(false);setHevyRaw('');setHevyErr('');}}
                  style={{width:'100%',padding:'11px',borderRadius:13,fontSize:14,marginTop:6,
                    background:'none',border:'none',cursor:'pointer',color:C.subtle,fontFamily:FONT_BODY}}>
                  Annulla
                </button>
              </div>
            </div>
          )}

          {showTextImport && (
            <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.40)',
              display:'flex',alignItems:'flex-end',justifyContent:'center'}}
              onClick={e=>{ if(e.target===e.currentTarget){ setShowTextImport(false); setTextRaw(''); }}}>
              <div style={{background:'#fff',borderRadius:'16px 16px 0 0',width:'100%',maxWidth:560,
                paddingBottom:'max(24px,env(safe-area-inset-bottom,16px))'}}>
                <div style={{width:36,height:4,borderRadius:2,background:'rgba(120,120,128,0.28)',margin:'12px auto 0'}}/>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px'}}>
                  <button onClick={()=>{ setShowTextImport(false); setTextRaw(''); }}
                    style={{background:'none',border:'none',fontFamily:FONT_BODY,fontSize:16,color:C.blue,cursor:'pointer',padding:0}}>
                    Annulla
                  </button>
                  <div style={{fontSize:17,fontWeight:600,color:C.text}}>Descrivi allenamento</div>
                  <div style={{width:60}}/>
                </div>
                <div style={{padding:'0 16px 12px'}}>
                  <textarea rows={6}
                    placeholder="es. 3x10 Squat 80 kg, 4x8 Panca 60 kg, ecc."
                    value={textRaw} onChange={e=>setTextRaw(e.target.value)}
                    maxLength={2000}
                    style={{width:'100%',borderRadius:14,border:'none',outline:'none',
                      background:'#F2F2F7',padding:'12px 14px',fontFamily:FONT_BODY,
                      fontSize:15,color:C.text,resize:'vertical',lineHeight:1.6,boxSizing:'border-box'}}/>
                  {textErr && (
                    <div style={{marginTop:8,padding:'10px 14px',background:'rgba(255,59,48,0.08)',
                      borderRadius:10,fontSize:13,color:C.red}}>
                      {textErr}
                    </div>
                  )}
                </div>
                <div style={{padding:'0 16px'}}>
                  <button onClick={()=>applyTextImport(true)}
                    disabled={!textRaw.trim()||textLoading}
                    style={{width:'100%',padding:'15px',borderRadius:14,border:'none',
                      background: textRaw.trim()&&!textLoading ? C.text : 'rgba(120,120,128,0.14)',
                      color: textRaw.trim()&&!textLoading ? '#fff' : C.subtle,
                      fontFamily:FONT_BODY,fontSize:16,fontWeight:700,cursor: textRaw.trim()&&!textLoading ? 'pointer' : 'default'}}>
                    {textLoading ? 'Analisi in corso...' : 'Genera'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Back button — torna alla lista schede solo se non siamo arrivati da initialPhase='import' */}
          <button onClick={()=>{ if(initialPhase==='import') onClose?.(); else setStep0Phase('list'); }}
            style={{background:'none',border:'none',cursor:'pointer',padding:'0 0 16px',
              fontSize:15,color:C.blue,fontFamily:FONT_BODY,display:'flex',alignItems:'center',gap:6}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 6l-6 6 6 6" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Indietro
          </button>

          <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:4}}>
            {scheda || 'Senza scheda'}
          </div>
          <div style={{fontSize:13,color:C.subtle,marginBottom:20}}>
            Scegli come importare la sessione
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {/* 1. Inserimento manuale */}
            <button onClick={()=>{ setStep0Phase('manual-picker'); }}
              style={{display:'flex',alignItems:'center',gap:16,padding:'16px 18px',
                borderRadius:18,border:'none',cursor:'pointer',
                background:'#F2F2F7',fontFamily:FONT_BODY,textAlign:'left',width:'100%'}}>
              <div style={{width:44,height:44,borderRadius:12,background:C.green+'15',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke={C.green} strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <div style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:2}}>Inserimento manuale</div>
                <div style={{fontSize:13,color:C.subtle}}>Inserisci esercizi e serie a mano</div>
              </div>
            </button>

            {/* 2. Importa da Hevy */}
            <button onClick={()=>{ setShowHevyImport(true); setHevyErr(''); }}
              style={{display:'flex',alignItems:'center',gap:16,padding:'16px 18px',
                borderRadius:18,border:'none',cursor:'pointer',
                background:'#F2F2F7',fontFamily:FONT_BODY,textAlign:'left',width:'100%'}}>
              <div style={{width:44,height:44,borderRadius:12,background:C.blue+'15',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 16V8M8 12l4-4 4 4" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="3" y="3" width="18" height="18" rx="4" stroke={C.blue} strokeWidth="1.5"/>
                </svg>
              </div>
              <div>
                <div style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:2}}>Importa da Hevy</div>
                <div style={{fontSize:13,color:C.subtle}}>Incolla il testo copiato dall'app Hevy</div>
              </div>
            </button>

            {/* 3. Descrivi allenamento — bloccato, presto disponibile */}
            <div style={{display:'flex',alignItems:'center',gap:16,padding:'16px 18px',
              borderRadius:18,background:'#F2F2F7',opacity:0.5,cursor:'default',
              fontFamily:FONT_BODY,position:'relative'}}>
              <div style={{width:44,height:44,borderRadius:12,background:C.purple+'15',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M8 6h13M8 12h13M8 18h7" stroke={C.purple} strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="4" cy="6" r="1.5" fill={C.purple}/>
                  <circle cx="4" cy="12" r="1.5" fill={C.purple}/>
                  <circle cx="4" cy="18" r="1.5" fill={C.purple}/>
                </svg>
              </div>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:2}}>
                  <div style={{fontSize:16,fontWeight:600,color:C.text}}>Descrivi allenamento</div>
                  <div style={{fontSize:11,fontWeight:600,color:C.purple,background:C.purple+'18',
                    borderRadius:20,padding:'2px 8px',letterSpacing:0.3}}>Presto</div>
                </div>
                <div style={{fontSize:13,color:C.subtle}}>Scrivi in testo libero, l'AI converte</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ fontFamily: FONT_BODY, padding: '0 0 8px' }}>
        {showHevyImport && (
          <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'flex-end',justifyContent:'center'}}
            onClick={e=>{if(e.target===e.currentTarget){setShowHevyImport(false);setHevyRaw('');setHevyErr('');}}}>
            <div style={{width:'100%',maxWidth:560,background:'#fff',borderRadius:'16px 16px 0 0',
              padding:'20px 20px max(24px,env(safe-area-inset-bottom,16px))'}}>
              <div style={{width:36,height:4,borderRadius:2,background:'rgba(120,120,128,0.28)',margin:'0 auto 16px'}}/>
              <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:4}}>Importa da Hevy</div>
              <div style={{fontSize:13,color:C.subtle,marginBottom:12,lineHeight:1.5}}>
                Copia il testo della sessione da Hevy (condividi sessione, copia testo) e incollalo qui.
              </div>
              <div style={{background:'#F2F2F7',borderRadius:10,padding:'10px 12px',marginBottom:10,
                fontSize:12,color:C.subtle,fontFamily:FONT_MONO,lineHeight:1.7,border:`1px dashed ${C.border}`}}>
                Dorso e tricipiti<br/>Il giorno venerdì, mag 15, 2026 alle ore 5:01pm<br/>Lat Pulldown (Cavo)<br/>Serie 1: 50 kg x 8 @ 9.5 rpe
              </div>
              <textarea value={hevyRaw} onChange={e=>{setHevyRaw(e.target.value);setHevyErr('');}}
                placeholder="Incolla qui le note da Hevy..."
                rows={7}
                style={{width:'100%',border:'none',borderRadius:12,padding:'12px 14px',
                  fontFamily:FONT_MONO,fontSize:13,color:C.text,outline:'none',
                  background:'#F2F2F7',boxSizing:'border-box',resize:'vertical',lineHeight:1.6}}/>
              {hevyErr && (
                <div style={{fontSize:13,color:C.red,padding:'8px 12px',background:C.redL,
                  borderRadius:10,marginTop:8,lineHeight:1.5}}>{hevyErr}</div>
              )}
              <button onClick={applyHevyImport} disabled={!hevyRaw.trim()}
                style={{...St.btn(C.blue),width:'100%',padding:'13px',borderRadius:13,
                  fontSize:15,marginTop:12,fontFamily:FONT_BODY,
                  opacity:hevyRaw.trim()?1:0.45}}>
                Importa sessione
              </button>
              <button onClick={()=>{setShowHevyImport(false);setHevyRaw('');setHevyErr('');}}
                style={{width:'100%',padding:'11px',borderRadius:13,fontSize:14,marginTop:6,
                  background:'none',border:'none',cursor:'pointer',color:C.subtle,fontFamily:FONT_BODY}}>
                Annulla
              </button>
            </div>
          </div>
        )}

        {showTextImport && (
          <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.40)',
            display:'flex',alignItems:'flex-end',justifyContent:'center'}}
            onClick={e=>{ if(e.target===e.currentTarget){ setShowTextImport(false); setTextRaw(''); }}}>
            <div style={{background:'#fff',borderRadius:'16px 16px 0 0',width:'100%',maxWidth:560,
              paddingBottom:'max(24px,env(safe-area-inset-bottom,16px))'}}>
              <div style={{width:36,height:4,borderRadius:2,background:'rgba(120,120,128,0.28)',margin:'12px auto 0'}}/>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px'}}>
                <button onClick={()=>{ setShowTextImport(false); setTextRaw(''); }}
                  style={{background:'none',border:'none',fontFamily:FONT_BODY,fontSize:16,color:C.blue,cursor:'pointer',padding:0}}>
                  Annulla
                </button>
                <div style={{fontSize:17,fontWeight:600,color:C.text}}>Descrivi allenamento</div>
                <div style={{width:60}}/>
              </div>
              <div style={{padding:'0 16px 12px'}}>
                <textarea rows={6}
                  placeholder="es. 3x10 Squat 80 kg, 4x8 Panca 60 kg, ecc."
                  value={textRaw} onChange={e=>setTextRaw(e.target.value)}
                  maxLength={2000}
                  style={{width:'100%',borderRadius:14,border:'none',outline:'none',
                    background:'#F2F2F7',padding:'12px 14px',fontFamily:FONT_BODY,
                    fontSize:15,color:C.text,resize:'vertical',lineHeight:1.6,boxSizing:'border-box'}}/>
                {textErr && (
                  <div style={{marginTop:8,padding:'10px 14px',background:'rgba(255,59,48,0.08)',
                    borderRadius:10,fontSize:13,color:C.red}}>
                    {textErr}
                  </div>
                )}
              </div>
              <div style={{padding:'0 16px'}}>
                <button onClick={applyTextImport} disabled={!textRaw.trim()||textLoading}
                  style={{width:'100%',padding:'15px',borderRadius:14,border:'none',
                    background: textRaw.trim()&&!textLoading ? C.text : 'rgba(120,120,128,0.14)',
                    color: textRaw.trim()&&!textLoading ? '#fff' : C.subtle,
                    fontFamily:FONT_BODY,fontSize:16,fontWeight:700,cursor: textRaw.trim()&&!textLoading ? 'pointer' : 'default'}}>
                  {textLoading ? 'Analisi in corso...' : 'Genera'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Inizia allenamento */}
        <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 8, marginTop: 8 }}>
          Inizia allenamento
        </div>
        {activeSchede.length === 0 && (
          <div style={{ padding: '28px 0 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 15, color: C.subtle }}>Nessuna scheda attiva.</div>
            <div style={{ fontSize: 13, color: C.subtle, opacity: 0.7 }}>Crea una scheda in Impostazioni per usarla qui.</div>
          </div>
        )}
        <div style={{ ...CARD_STYLE, overflow: 'hidden', padding: 0, background: '#F2F2F7', boxShadow: 'none', border: 'none' }}>
          {allRows.map((row, i) => (
            <div key={row.name} style={{ position: 'relative' }}>
              <button
                onClick={() => pickScheda(row.empty ? '' : row.name)}
                onPointerDown={e => { e.currentTarget.style.background = 'rgba(120,120,128,0.08)'; }}
                onPointerUp={e => { e.currentTarget.style.background = 'none'; }}
                onPointerCancel={e => { e.currentTarget.style.background = 'none'; }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '14px 16px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: FONT_BODY, textAlign: 'left',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'background 0.1s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {row.empty
                    ? <div style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${C.border}`, flexShrink: 0 }} />
                    : <div style={{ width: 12, height: 12, borderRadius: '50%', background: row.dot, flexShrink: 0 }} />
                  }
                  <span style={{ fontSize: 16, color: row.empty ? C.subtle : C.text, fontWeight: row.empty ? 400 : 500 }}>
                    {row.name}
                  </span>
                </div>
                {CHEVRON}
              </button>
              {i < allRows.length - 1 && (
                <div style={{ height: '0.5px', background: 'rgba(60,60,67,0.18)', marginLeft: 40, marginRight: 0 }} />
              )}
            </div>
          ))}
        </div>

      </div>
    );
  }

  // ── form (chat-session stepper) ─────────────────────────────────────────
  const intens  = _globalSettings.intensitaAttiva===false ? 'off' : (_globalSettings.intensita||'entrambi');
  const showRPE = intens==='rpe' || intens==='entrambi';
  const showRIR = intens==='rir' || intens==='entrambi';
  const setFields  = [
    {f:'weight',step:'0.5',ph:'0',  required:true},
    {f:'reps',  step:'1',  ph:'0',  required:true},
    ...(showRPE?[{f:'rpe',step:'0.5',ph:'—',required:false}]:[]),
    ...(showRIR?[{f:'rir',step:'0.5',ph:'—',required:false}]:[]),
  ];

  // Stepper helpers
  const validExList = exList.filter(e => e.name.trim());
  const safeIdx = Math.min(currentExIdx, Math.max(0, validExList.length - 1));
  const currentEx = validExList[safeIdx] || exList[0];
  const currentEi = exList.indexOf(currentEx);
  const isLastEx = safeIdx >= validExList.length - 1;
  const currentHasData = currentEx?.sets?.some(s => parseFloat(s.weight) > 0 && parseInt(s.reps) > 0);

  const goNextEx = () => {
    if (!isLastEx) { setCurrentExIdx(i => i + 1); setActiveExTab(null); }
    else save();
  };


  // prevSets for current exercise (used in grid)
  const prevSessForEx = currentEx?.name
    ? [...db.sessions].sort((a,b) => b.date.localeCompare(a.date))
      .find(s => s.exercises.some(e => e.name.toLowerCase() === currentEx.name.toLowerCase()))
    : null;
  const prevSetsForEx = prevSessForEx?.exercises
    .find(e => e.name.toLowerCase() === currentEx?.name?.toLowerCase())
    ?.sets?.filter(s => parseFloat(s.weight) > 0 || parseInt(s.reps) > 0) || [];

  const noCheckbox = isImportMode || isManualEntry;
  const gridColsNew = `18px 1fr 1fr 1fr${showRPE?' 1fr':''}${showRIR?' 1fr':''}${noCheckbox?'':' 28px'}`;
  const setHeadersNew = ['PREC.','KG','REPS',...(showRPE?['RPE']:[]),...(showRIR?['RIR']:[])];

  const timerSec = restTimerState.remaining;
  const timerMM = String(Math.floor(timerSec / 60)).padStart(2, '0');
  const timerSS = String(timerSec % 60).padStart(2, '0');
  const timerColor = timerSec <= 10 ? C.red : C.green;

  return (
    <div style={{fontFamily:FONT_BODY, display:'flex', flexDirection:'column', height:'100%', minHeight:0}}>

      {/* ── Import overlays ─────────────────────────────────────────── */}
      {showHevyImport && (
        <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'flex-end',justifyContent:'center'}}
          onClick={e=>{if(e.target===e.currentTarget){setShowHevyImport(false);setHevyRaw('');setHevyErr('');}}}>
          <div style={{width:'100%',maxWidth:560,background:'#fff',borderRadius:'16px 16px 0 0',
            padding:'20px 20px max(24px,env(safe-area-inset-bottom,16px))'}}>
            <div style={{width:36,height:4,borderRadius:2,background:'rgba(120,120,128,0.28)',margin:'0 auto 16px'}}/>
            <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:4}}>Importa da Hevy</div>
            <div style={{fontSize:13,color:C.subtle,marginBottom:12,lineHeight:1.5}}>
              Copia il testo della sessione da Hevy (condividi sessione, copia testo) e incollalo qui.
            </div>
            <div style={{background:'#F2F2F7',borderRadius:10,padding:'10px 12px',marginBottom:10,
              fontSize:12,color:C.subtle,fontFamily:FONT_MONO,lineHeight:1.7,border:`1px dashed ${C.border}`}}>
              Dorso e tricipiti<br/>Il giorno venerdì, mag 15, 2026 alle ore 5:01pm<br/>Lat Pulldown (Cavo)<br/>Serie 1: 50 kg x 8 @ 9.5 rpe
            </div>
            <textarea value={hevyRaw} onChange={e=>{setHevyRaw(e.target.value);setHevyErr('');}}
              placeholder="Incolla qui le note da Hevy..."
              rows={7}
              style={{width:'100%',border:'none',borderRadius:12,padding:'12px 14px',
                fontFamily:FONT_MONO,fontSize:13,color:C.text,outline:'none',
                background:'#F2F2F7',boxSizing:'border-box',resize:'vertical',lineHeight:1.6}}/>
            {hevyErr && (
              <div style={{fontSize:13,color:C.red,padding:'8px 12px',background:C.redL,
                borderRadius:10,marginTop:8,lineHeight:1.5}}>{hevyErr}</div>
            )}
            <button onClick={applyHevyImport} disabled={!hevyRaw.trim()}
              style={{...St.btn(C.blue),width:'100%',padding:'13px',borderRadius:13,
                fontSize:15,marginTop:12,fontFamily:FONT_BODY,
                opacity:hevyRaw.trim()?1:0.45}}>
              Importa sessione
            </button>
            <button onClick={()=>{setShowHevyImport(false);setHevyRaw('');setHevyErr('');}}
              style={{width:'100%',padding:'11px',borderRadius:13,fontSize:14,marginTop:6,
                background:'none',border:'none',cursor:'pointer',color:C.subtle,fontFamily:FONT_BODY}}>
              Annulla
            </button>
          </div>
        </div>
      )}

      {showTextImport && (
        <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.40)',
          display:'flex',alignItems:'flex-end',justifyContent:'center'}}
          onClick={e=>{ if(e.target===e.currentTarget){ setShowTextImport(false); setTextRaw(''); }}}>
          <div style={{background:'#fff',borderRadius:'16px 16px 0 0',width:'100%',maxWidth:560,
            paddingBottom:'max(24px,env(safe-area-inset-bottom,16px))'}}>
            <div style={{width:36,height:4,borderRadius:2,background:'rgba(120,120,128,0.28)',margin:'12px auto 0'}}/>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px'}}>
              <button onClick={()=>{ setShowTextImport(false); setTextRaw(''); }}
                style={{background:'none',border:'none',fontFamily:FONT_BODY,fontSize:16,color:C.blue,cursor:'pointer',padding:0}}>
                Annulla
              </button>
              <div style={{fontSize:17,fontWeight:600,color:C.text}}>Descrivi allenamento</div>
              <div style={{width:60}}/>
            </div>
            <div style={{padding:'0 16px 12px'}}>
              <textarea rows={6}
                placeholder="es. 3x10 Squat 80 kg, 4x8 Panca 60 kg, ecc."
                value={textRaw} onChange={e=>setTextRaw(e.target.value)}
                maxLength={2000}
                style={{width:'100%',borderRadius:14,border:'none',outline:'none',
                  background:'#F2F2F7',padding:'12px 14px',fontFamily:FONT_BODY,
                  fontSize:15,color:C.text,resize:'vertical',lineHeight:1.6,boxSizing:'border-box'}}/>
              {textErr && (
                <div style={{marginTop:8,padding:'10px 14px',background:'rgba(255,59,48,0.08)',
                  borderRadius:10,fontSize:13,color:C.red}}>
                  {textErr}
                </div>
              )}
            </div>
            <div style={{padding:'0 16px'}}>
              <button onClick={applyTextImport} disabled={!textRaw.trim()||textLoading}
                style={{width:'100%',padding:'15px',borderRadius:14,border:'none',
                  background: textRaw.trim()&&!textLoading ? C.text : 'rgba(120,120,128,0.14)',
                  color: textRaw.trim()&&!textLoading ? '#fff' : C.subtle,
                  fontFamily:FONT_BODY,fontSize:16,fontWeight:700,cursor: textRaw.trim()&&!textLoading ? 'pointer' : 'default'}}>
                {textLoading ? 'Analisi in corso...' : 'Genera'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stepper header (nascosto in manual mode) ────────────────── */}
      {!isManualEntry && (
      <div style={{flexShrink:0, paddingBottom:12}}>
        {currentEx && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:17,fontWeight:800,color:C.text,letterSpacing:-0.3,
                whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                {currentEx.name || 'Esercizio'}
              </div>
              {currentEx.name.trim()&&(()=>{
                const dbEx=findExercise(currentEx.name);
                return dbEx?.primaryMuscle
                  ? <div style={{fontSize:12,color:C.subtle,marginTop:1,textTransform:'capitalize'}}>{dbEx.primaryMuscle}</div>
                  : null;
              })()}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
              <span style={{fontSize:13,color:C.subtle,fontWeight:600,
                background:'rgba(120,120,128,0.10)',borderRadius:20,padding:'3px 10px',
                fontFamily:FONT_MONO,letterSpacing:0.5}}>
                {String(Math.floor(elapsedSec/60)).padStart(2,'0')}:{String(elapsedSec%60).padStart(2,'0')}
              </span>
              <span style={{fontSize:13,color:C.subtle,fontWeight:500}}>
                {safeIdx+1}/{validExList.length}
              </span>
              {/* 3-dot menu */}
              <div style={{position:'relative'}}>
                <button
                  onClick={()=>setExMenuOpen(exMenuOpen===currentEi?null:currentEi)}
                  style={{width:32,height:32,borderRadius:100,background:'rgba(120,120,128,0.10)',
                    border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',
                    WebkitTapHighlightColor:'transparent'}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="5" cy="12" r="1.5" fill={C.muted}/>
                    <circle cx="12" cy="12" r="1.5" fill={C.muted}/>
                    <circle cx="19" cy="12" r="1.5" fill={C.muted}/>
                  </svg>
                </button>
                {exMenuOpen===currentEi&&(
                  <div style={{position:'absolute',top:38,right:0,zIndex:300,
                    background:'#fff',borderRadius:10,boxShadow:'0 2px 12px rgba(0,0,0,0.08)',
                    minWidth:180,overflow:'hidden',border:'1px solid #EAEAEA'}}>
                    <button
                      onClick={()=>{setReplacingIdx(currentEi);setExMenuOpen(null);setExFocus(currentEi);setExSearch(s=>({...s,[currentEi]:''}));}}
                      style={{width:'100%',padding:'13px 16px',background:'none',border:'none',
                        cursor:'pointer',fontFamily:FONT_BODY,fontSize:15,color:C.text,textAlign:'left',display:'block'}}>
                      Sostituisci esercizio
                    </button>
                    <div style={{height:'0.5px',background:C.fill}}/>
                    <button
                      onClick={()=>{remEx(currentEi);setExMenuOpen(null);setCurrentExIdx(i=>Math.max(0,i-1));}}
                      disabled={exList.length<=1}
                      style={{width:'100%',padding:'13px 16px',background:'none',border:'none',
                        cursor:exList.length<=1?'default':'pointer',fontFamily:FONT_BODY,fontSize:15,
                        color:exList.length<=1?C.subtle:C.red,textAlign:'left',display:'block'}}>
                      Rimuovi esercizio
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* ── Rest timer (nascosto in import/manual mode) ──────────────── */}
      {!(isImportMode || isManualEntry) && <div style={{flexShrink:0,paddingBottom:10}}>
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 14px',
          background:'#F2F2F7',borderRadius:12}}>
          <span style={{fontSize:13,color:C.subtle,flexShrink:0}}>Riposo (s):</span>
          <input
            type="number" min="10" max="600" step="5"
            value={restTimerDuration}
            onChange={e=>setRestTimerDuration(Math.max(10,parseInt(e.target.value)||90))}
            style={{width:64,border:'none',borderRadius:8,padding:'5px 8px',
              fontFamily:FONT_MONO,fontSize:14,color:C.text,outline:'none',
              background:'#E8E8ED',textAlign:'center'}}/>
          {restTimerState.running && (
            <>
              <span style={{fontSize:22,fontWeight:700,color:timerColor,fontFamily:FONT_MONO,
                letterSpacing:1,flex:1,textAlign:'center'}}>
                {timerMM}:{timerSS}
              </span>
              <button onClick={stopRestTimer}
                style={{background:'none',border:'none',cursor:'pointer',
                  fontSize:12,color:C.subtle,fontFamily:FONT_BODY,flexShrink:0,padding:0}}>
                Annulla
              </button>
            </>
          )}
        </div>
      </div>}

      {/* ── Scrollable body ─────────────────────────────────────────── */}
      <div ref={scrollBodyRef} style={{flex:1,overflowY:'auto',WebkitOverflowScrolling:'touch'}}>

        {/* ── Inserimento manuale: tutti gli esercizi in un'unica pagina ── */}
        {isManualEntry && (
          <div>
            {validExList.map((ex, vi) => {
              const ei = exList.indexOf(ex);
              const prevSess = ex.name
                ? [...db.sessions].sort((a,b)=>b.date.localeCompare(a.date))
                  .find(s=>s.exercises.some(e=>e.name.toLowerCase()===ex.name.toLowerCase()))
                : null;
              const prevSets = prevSess?.exercises
                .find(e=>e.name.toLowerCase()===ex.name.toLowerCase())
                ?.sets?.filter(s=>parseFloat(s.weight)>0||parseInt(s.reps)>0)||[];
              const dbEx = findExercise(ex.name);
              return (
                <div key={ei} style={{marginBottom:20}}>
                  {/* Exercise header */}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:16,fontWeight:700,color:C.text,letterSpacing:-0.2,
                        whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                        {ex.name || 'Esercizio'}
                      </div>
                      {dbEx?.primaryMuscle && (
                        <div style={{fontSize:12,color:C.subtle,marginTop:1,textTransform:'capitalize'}}>{dbEx.primaryMuscle}</div>
                      )}
                    </div>
                    <div style={{position:'relative'}}>
                      <button onClick={()=>setExMenuOpen(exMenuOpen===ei?null:ei)}
                        style={{width:30,height:30,borderRadius:100,background:'rgba(120,120,128,0.10)',
                          border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',
                          WebkitTapHighlightColor:'transparent'}}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <circle cx="5" cy="12" r="1.5" fill={C.muted}/>
                          <circle cx="12" cy="12" r="1.5" fill={C.muted}/>
                          <circle cx="19" cy="12" r="1.5" fill={C.muted}/>
                        </svg>
                      </button>
                      {exMenuOpen===ei&&(
                        <div style={{position:'absolute',top:36,right:0,zIndex:300,
                          background:'#fff',borderRadius:10,boxShadow:'0 2px 12px rgba(0,0,0,0.08)',
                          minWidth:180,overflow:'hidden',border:'1px solid #EAEAEA'}}>
                          <button onClick={()=>{setReplacingIdx(ei);setExMenuOpen(null);setExSearch(s=>({...s,[ei]:''}));}}
                            style={{width:'100%',padding:'13px 16px',background:'none',border:'none',
                              cursor:'pointer',fontFamily:FONT_BODY,fontSize:15,color:C.text,textAlign:'left',display:'block'}}>
                            Sostituisci esercizio
                          </button>
                          <div style={{height:'0.5px',background:C.fill}}/>
                          <button onClick={()=>{remEx(ei);setExMenuOpen(null);}}
                            disabled={exList.length<=1}
                            style={{width:'100%',padding:'13px 16px',background:'none',border:'none',
                              cursor:exList.length<=1?'default':'pointer',fontFamily:FONT_BODY,fontSize:15,
                              color:exList.length<=1?C.subtle:C.red,textAlign:'left',display:'block'}}>
                            Rimuovi esercizio
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Ultima volta — hint contestuale */}
                  {prevSets.length > 0
                    ? <div style={{fontSize:12,color:C.subtle,marginBottom:6}}>
                        Ultima volta: {fmtLastSession(prevSets, prevSess?.date)}
                      </div>
                    : ex.name
                      ? <div style={{fontSize:12,color:C.muted,marginBottom:6}}>Prima volta con questo esercizio</div>
                      : null
                  }
                  {/* Set grid */}
                  <div style={EX_CARD}>
                    <div style={{display:'grid',gridTemplateColumns:gridColsNew,gap:6,marginBottom:6,paddingBottom:4}}>
                      <div/>
                      {setHeadersNew.map((h,i)=>(
                        <div key={i} style={{fontSize:10,fontWeight:700,color:C.subtle,
                          textAlign:'center',letterSpacing:0.8,textTransform:'uppercase'}}>{h}</div>
                      ))}
                    </div>
                    {ex.sets.map((s,si)=>{
                      const prevS = prevSets[si];
                      return (
                        <div key={si} style={{display:'grid',gridTemplateColumns:gridColsNew,gap:6,paddingTop:6,paddingBottom:6,alignItems:'center'}}>
                          <div style={{fontSize:12,color:C.subtle,textAlign:'center',fontFamily:FONT_MONO,fontWeight:500}}>{si+1}</div>
                          <div style={{fontSize:11,color:C.subtle,textAlign:'center',lineHeight:1.3}}>
                            {prevS
                              ? <>{prevS.weight}×{prevS.reps}{showRPE&&prevS.rpe?<><br/><span style={{fontSize:10}}>@{prevS.rpe}</span></>:null}</>
                              : <span style={{color:C.fill}}>—</span>}
                          </div>
                          {setFields.map(({f,step,ph})=>(
                            <input key={f}
                              style={{...St.monoIn,padding:'10px 4px',fontSize:15,
                                background:'#ffffff',border:'1px solid rgba(60,60,67,0.18)',
                                borderRadius:10,outline:'none',color:C.text,
                                fontFamily:FONT_MONO,fontWeight:600,minWidth:0,boxSizing:'border-box'}}
                              type="number" inputMode="numeric" pattern="[0-9]*" min="0" step={step} placeholder={ph}
                              value={s[f]} onChange={e=>updSet(ei,si,f,e.target.value)}/>
                          ))}
                        </div>
                      );
                    })}
                    <div style={{display:'flex',alignItems:'center',marginTop:8,paddingBottom:4}}>
                      <button onClick={()=>addSet(ei)}
                        style={{flex:1,padding:'9px 0',background:'none',border:'none',
                          cursor:'pointer',fontSize:13,fontWeight:500,fontFamily:FONT_BODY,
                          WebkitTapHighlightColor:'transparent',
                          display:'flex',alignItems:'center',justifyContent:'center',gap:4}}>
                        <Plus width={12} height={12} color={C.muted} strokeWidth={2}/>
                        <span style={{color:C.muted}}>Aggiungi set</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Aggiungi esercizio */}
            <button onClick={()=>addEx()}
              style={{width:'100%',padding:'12px',marginBottom:12,
                background:'none',border:'1px dashed rgba(60,60,67,0.22)',
                borderRadius:12,fontSize:15,color:C.blue,cursor:'pointer',
                fontFamily:FONT_BODY}}>
              + Aggiungi esercizio
            </button>
            {/* Data + Durata */}
            <div style={{background:'#F2F2F7',borderRadius:16,marginBottom:16,overflow:'hidden'}}>
              <div style={{padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                <span style={{fontSize:15,color:C.subtle,fontWeight:400,flexShrink:0}}>Durata (min) <span style={{fontSize:12,color:'#B0B0B8'}}>opzionale</span></span>
                <input style={{...St.monoIn,textAlign:'right',width:90,padding:'8px 12px',
                  fontSize:15,flexShrink:0,border:'none',borderRadius:10,outline:'none',
                  background:'#E8E8ED',fontFamily:FONT_MONO}}
                  type="number" min="1" max="300" placeholder="—"
                  value={duration} onChange={e=>setDuration(e.target.value)}/>
              </div>
              <div style={{height:'0.5px',background:'rgba(60,60,67,0.18)',margin:'0 16px'}}/>
              <div style={{padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                <span style={{fontSize:15,color:C.subtle,fontWeight:400,flexShrink:0}}>Data <span style={{fontSize:12,color:'#B0B0B8'}}>opzionale</span></span>
                <input style={{border:'none',borderRadius:10,outline:'none',background:'#E8E8ED',
                  fontFamily:FONT_BODY,fontSize:15,padding:'8px 12px',color:C.text}}
                  type="date" value={date} onChange={e=>setDate(e.target.value)}/>
              </div>
            </div>
            {saveErr&&(
              <div style={{fontSize:13,color:C.red,fontWeight:600,textAlign:'center',
                marginBottom:12,padding:'10px',background:C.redL,borderRadius:12}}>
                {saveErr}
              </div>
            )}
          </div>
        )}

        {/* Coach AI — consiglio per questo esercizio (solo sessione live) */}
        {!isManualEntry && !isImportMode && currentEx?.name && (() => {
          const advice = getCoachAdvice(scheda, currentEx.name);
          if (!advice) return null;
          const STATO_COL = {
            progressione: { bg:'#E5F9EC', color:'#1A9E45', border:'#A2EBBF' },
            pr:           { bg:'#E5F1FF', color:'#0060CC', border:'#A8CBFF' },
            stallo:       { bg:'#FFF3E0', color:'#CC7000', border:'#FFD080' },
            regressione:  { bg:'#FFECEB', color:'#CC2800', border:'#FFB3AF' },
          };
          const sc = STATO_COL[(advice.stato||'').toLowerCase()] || { bg:'#F2F2F7', color:C.subtle, border:C.fill };
          return (
            <div style={{
              marginBottom:10,
              background:'#fff',
              borderRadius:14,
              border:`1px solid ${sc.border}`,
              padding:'10px 13px 10px',
              boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
            }}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                <div style={{display:'flex',alignItems:'center',gap:5}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={sc.color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8 2 5 5 5 9c0 3 1.5 5.5 4 7.5L12 20l3-3.5C17.5 14.5 19 12 19 9c0-4-3-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:0.3,color:sc.color,textTransform:'uppercase'}}>Coach AI</span>
                </div>
                {advice.stato && (
                  <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,
                    background:sc.bg,color:sc.color,border:`1px solid ${sc.border}`}}>
                    {advice.stato.charAt(0).toUpperCase()+advice.stato.slice(1)}
                  </span>
                )}
              </div>
              {advice.obiettivo && (
                <div style={{fontSize:13,fontWeight:600,color:'#0055CC',lineHeight:1.45,marginBottom:advice.analisi?5:0}}>
                  {advice.obiettivo}
                </div>
              )}
              {advice.analisi && (
                <div style={{fontSize:12,color:C.muted,lineHeight:1.45}}>
                  {advice.analisi}
                </div>
              )}
            </div>
          );
        })()}

        {/* Set grid */}
        {currentEx && (
          <div style={EX_CARD}>
            {/* Grid header */}
            <div style={{display:'grid',gridTemplateColumns:gridColsNew,gap:6,marginBottom:6,paddingBottom:4}}>
              <div/>
              {setHeadersNew.map((h,i)=>(
                <div key={i} style={{fontSize:10,fontWeight:700,color:C.subtle,
                  textAlign:'center',letterSpacing:0.8,textTransform:'uppercase'}}>{h}</div>
              ))}
              {!noCheckbox && <div/>}
            </div>

            {/* Set rows */}
            {currentEx.sets.map((s,si)=>{
              const prevS = prevSetsForEx[si];
              const isDone = parseFloat(s.weight) > 0 && parseInt(s.reps) > 0;
              const fb = setFeedback[currentEi];
              return (
                <div key={si}>
                  <div style={{display:'grid',gridTemplateColumns:gridColsNew,gap:6,paddingTop:6,paddingBottom:6,alignItems:'center'}}>
                    <div style={{fontSize:12,color:C.subtle,textAlign:'center',fontFamily:FONT_MONO,fontWeight:500}}>
                      {si+1}
                    </div>
                    {/* Precedente */}
                    <div style={{fontSize:11,color:C.subtle,textAlign:'center',lineHeight:1.3}}>
                      {prevS
                        ? <>{prevS.weight}×{prevS.reps}{showRPE&&prevS.rpe?<><br/><span style={{fontSize:10}}>@{prevS.rpe}</span></>:null}</>
                        : <span style={{color:C.fill}}>—</span>
                      }
                    </div>
                    {/* Input fields */}
                    {setFields.map(({f,step,ph})=>(
                      <input key={f}
                        style={{...St.monoIn,padding:'10px 4px',fontSize:15,
                          background:'#ffffff',border:'1px solid rgba(60,60,67,0.18)',
                          borderRadius:10,outline:'none',color:C.text,
                          fontFamily:FONT_MONO,fontWeight:600,minWidth:0,boxSizing:'border-box'}}
                        type="number" inputMode="numeric" pattern="[0-9]*" min="0" step={step} placeholder={ph}
                        value={s[f]} onChange={e=>updSet(currentEi,si,f,e.target.value)}/>
                    ))}
                    {/* Checkbox visiva — solo in sessione live */}
                    {!noCheckbox && (() => {
                      const isChecked = !!(doneSets[currentEi]?.has(si));
                      return (
                        <button onClick={()=>{
                          setDoneSets(d=>{
                            const cur = new Set(d[currentEi]||[]);
                            if(cur.has(si)) cur.delete(si); else { cur.add(si); startRestTimer(); }
                            return {...d,[currentEi]:cur};
                          });
                        }}
                          style={{background:'none',border:'none',padding:0,cursor:'pointer',
                            display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10"
                              stroke={isChecked ? C.green : 'rgba(120,120,128,0.28)'} strokeWidth="1.75"
                              fill={isChecked ? `${C.green}15` : 'none'}/>
                            {isChecked && <path d="M8 12l3 3 5-5" stroke={C.green} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>}
                          </svg>
                        </button>
                      );
                    })()}
                  </div>
                  {/* Delta chip — ogni serie compilata */}
                  {fb&&fb.chipText&&isDone&&(
                    <div style={{marginBottom:4,display:'flex',alignItems:'center',gap:6}}>
                      <span style={{fontSize:12,fontWeight:700,color:fb.chipColor,
                        background:`${fb.chipColor}15`,borderRadius:8,padding:'2px 8px',fontFamily:FONT_BODY}}>
                        {fb.chipText}
                      </span>
                      {si===0&&fb.deltaText&&<span style={{fontSize:12,color:fb.deltaColor,fontFamily:FONT_BODY}}>{fb.deltaText}</span>}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Aggiungi set */}
            <div style={{display:'flex',alignItems:'center',marginTop:8,paddingBottom:4}}>
              <button onClick={()=>addSet(currentEi)}
                style={{flex:1,padding:'9px 0',background:'none',border:'none',
                  cursor:'pointer',fontSize:13,fontWeight:500,fontFamily:FONT_BODY,
                  WebkitTapHighlightColor:'transparent',
                  display:'flex',alignItems:'center',justifyContent:'center',gap:4}}>
                <Plus width={12} height={12} color={C.muted} strokeWidth={2}/>
                <span style={{color:C.muted}}>Aggiungi set</span>
              </button>
            </div>
          </div>
        )}

        {/* Pill tabs: Grafici / Analisi / Confronto — solo sessione live */}
        {!isManualEntry && !isImportMode && currentEx?.name && (
          <div style={{marginBottom:8}}>
            <div style={{display:'flex',gap:6,marginBottom:0}}>
              {['Grafici','Analisi','Confronto'].map(tab=>{
                const key = tab.toLowerCase();
                const isActive = activeExTab === key;
                return (
                  <button key={tab}
                    onClick={()=>{
                      if(key==='grafici'){ setShowProgresso(p=>!p); setActiveExTab(null); return; }
                      setActiveExTab(isActive ? null : key);
                      setShowProgresso(false);
                    }}
                    style={{padding:'7px 14px',borderRadius:20,border:'none',cursor:'pointer',
                      fontFamily:FONT_BODY,fontSize:13,fontWeight:600,
                      background: isActive ? C.text : 'rgba(120,120,128,0.10)',
                      color: isActive ? '#fff' : C.subtle,
                      transition:'background 0.15s,color 0.15s',
                      WebkitTapHighlightColor:'transparent'}}>
                    {tab}
                  </button>
                );
              })}
            </div>
            {showProgresso && (
              <div style={{marginTop:12,marginBottom:8}}>
                <ProgressoPanel exName={currentEx.name} sessions={db.sessions}/>
              </div>
            )}
            {activeExTab==='analisi' && (
              <div style={{marginTop:12,marginBottom:8}}>
                <SessionAnalysisPanel
                  session={{...{exercises:exList.filter(e=>e.name.trim())},workoutName:scheda,date,id:0}}
                  allSessions={db.sessions}/>
              </div>
            )}
            {activeExTab==='confronto' && (
              <div style={{marginTop:12,marginBottom:8}}>
                <ProgressoPanel exName={currentEx.name} sessions={db.sessions}/>
              </div>
            )}
          </div>
        )}


        {/* Aggiungi esercizio (solo ultimo step, non manual) */}
        {!isManualEntry && isLastEx && (
          <button onClick={()=>{ addEx(); setCurrentExIdx(validExList.length); }}
            style={{width:'100%',padding:'12px',marginBottom:12,
              background:'none',border:'1px dashed rgba(60,60,67,0.22)',
              borderRadius:12,fontSize:15,color:C.blue,cursor:'pointer',
              fontFamily:FONT_BODY}}>
            + Aggiungi esercizio
          </button>
        )}

        {/* Durata + Data (last exercise only, non manual) */}
        {!isManualEntry && isLastEx && (
          <div style={{background:'#F2F2F7',borderRadius:16,marginBottom:16,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
              <span style={{fontSize:15,color:C.subtle,fontWeight:400,flexShrink:0}}>Durata (min) <span style={{fontSize:12,color:'#B0B0B8'}}>opzionale</span></span>
              <input style={{...St.monoIn,textAlign:'right',width:90,padding:'8px 12px',
                fontSize:15,flexShrink:0,border:'none',borderRadius:10,outline:'none',
                background:'#E8E8ED',fontFamily:FONT_MONO}}
                type="number" min="1" max="300" placeholder="—"
                value={duration} onChange={e=>setDuration(e.target.value)}/>
            </div>
            <div style={{height:'0.5px',background:'rgba(60,60,67,0.18)',margin:'0 16px'}}/>
            <div style={{padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
              <span style={{fontSize:15,color:C.subtle,fontWeight:400,flexShrink:0}}>Data <span style={{fontSize:12,color:'#B0B0B8'}}>opzionale</span></span>
              <input style={{border:'none',borderRadius:10,outline:'none',background:'#E8E8ED',
                fontFamily:FONT_BODY,fontSize:15,padding:'8px 12px',color:C.text}}
                type="date" value={date} onChange={e=>setDate(e.target.value)}/>
            </div>
          </div>
        )}

        {!isManualEntry && saveErr&&(
          <div style={{fontSize:13,color:C.red,fontWeight:600,textAlign:'center',
            marginBottom:12,padding:'10px',background:C.redL,borderRadius:12}}>
            {saveErr}
          </div>
        )}

      </div>{/* end scrollable body */}

      {/* ── Sticky footer ───────────────────────────────────────────── */}
      <div style={{flexShrink:0,paddingTop:12,
        paddingBottom:'max(16px, env(safe-area-inset-bottom, 16px))',
        borderTop:'0.5px solid rgba(60,60,67,0.10)',
        display:'flex',flexDirection:'column',gap:8}}>
        {isManualEntry ? (<>
          <button onClick={()=>save()}
            className="gp-btn-press"
            style={{width:'100%',padding:'14px',borderRadius:14,border:'none',
              background:C.text,color:'#fff',
              fontFamily:FONT_BODY,fontSize:16,fontWeight:700,cursor:'pointer',
              transition:'background 0.15s,color 0.15s'}}>
            Salva sessione
          </button>
          <button onClick={()=>{ if(window.confirm('Vuoi uscire? I dati non saranno salvati.')) onClose(); }}
            className="gp-btn-press"
            style={{width:'100%',padding:'13px',borderRadius:14,border:'none',
              background:'rgba(255,59,48,0.10)',color:'#FF3B30',
              fontFamily:FONT_BODY,fontSize:15,fontWeight:600,cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',gap:6,
              transition:'background 0.15s,color 0.15s'}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FF3B30" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
            Chiudi
          </button>
        </>) : (<>
          <button onClick={goNextEx} disabled={!currentHasData}
            className="gp-btn-press"
            style={{width:'100%',padding:'14px',borderRadius:14,border:'none',
              background:currentHasData?C.text:'rgba(120,120,128,0.14)',
              color:currentHasData?'#fff':C.subtle,
              fontFamily:FONT_BODY,fontSize:16,fontWeight:700,
              cursor:currentHasData?'pointer':'default',
              transition:'background 0.15s,color 0.15s'}}>
            {isLastEx ? 'Salva sessione' : 'Avanti'}
          </button>
          {safeIdx===0 ? (
            <button onClick={()=>{ if(window.confirm('Vuoi abbandonare l\'allenamento? I dati non saranno salvati.')) onClose(); }}
              className="gp-btn-press"
              style={{width:'100%',padding:'13px',borderRadius:14,border:'none',
                background:'rgba(255,59,48,0.10)',color:'#FF3B30',
                fontFamily:FONT_BODY,fontSize:15,fontWeight:600,cursor:'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:6,
                transition:'background 0.15s,color 0.15s'}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FF3B30" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
              Abbandona
            </button>
          ) : (
            <button onClick={()=>{ setCurrentExIdx(i=>i-1); setShowProgresso(false); }}
              className="gp-btn-press"
              style={{width:'100%',padding:'13px',borderRadius:14,border:'none',
                background:'rgba(120,120,128,0.12)',color:C.text,
                fontFamily:FONT_BODY,fontSize:15,fontWeight:600,cursor:'pointer',
                transition:'background 0.15s,color 0.15s'}}>
              Indietro
            </button>
          )}
        </>)}
      </div>

      {/* Replace exercise overlay */}
      {replacingIdx!==null&&(
        <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.45)',
          display:'flex',alignItems:'flex-end',justifyContent:'center'}}
          onClick={e=>{if(e.target===e.currentTarget){setReplacingIdx(null);setExSearch(s=>{const n={...s};delete n[replacingIdx];return n;});setExFocus(null);}}}>
          <div style={{width:'100%',maxWidth:560,background:'#fff',borderRadius:'16px 16px 0 0',
            padding:'20px 20px max(32px,env(safe-area-inset-bottom,24px))',maxHeight:'70vh',
            display:'flex',flexDirection:'column'}}>
            <div style={{width:36,height:4,borderRadius:2,background:'rgba(120,120,128,0.28)',margin:'0 auto 16px'}}/>
            <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:12}}>Sostituisci esercizio</div>
            <input autoFocus
              style={{...GRAY_IN,marginBottom:12}}
              placeholder="Cerca esercizio..."
              value={exSearch[replacingIdx]||''}
              onChange={e=>{
                const val=e.target.value;
                setExSearch(s=>({...s,[replacingIdx]:val}));
              }}
            />
            <div style={{overflowY:'auto',flex:1}}>
              {(()=>{
                const q=(exSearch[replacingIdx]||'').toLowerCase().trim();
                const hits=q.length===0?EXERCISE_DB.slice(0,20):EXERCISE_DB.filter(e=>
                  e.name.toLowerCase().includes(q)||(e.aliases||[]).some(a=>a.toLowerCase().includes(q))
                ).slice(0,20);
                return hits.map((e,hi)=>(
                  <div key={e.name}>
                    <button
                      onClick={()=>{
                        updEx(replacingIdx,'name',e.name);
                        updEx(replacingIdx,'type',e.type);
                        setExSearch(s=>{const n={...s};delete n[replacingIdx];return n;});
                        setReplacingIdx(null);
                      }}
                      style={{width:'100%',background:'none',border:'none',cursor:'pointer',
                        padding:'12px 0',display:'flex',alignItems:'center',gap:10,textAlign:'left'}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:15,fontWeight:500,color:C.text}}>{e.name}</div>
                        {e.primaryMuscle&&<div style={{fontSize:12,color:C.subtle,marginTop:1,textTransform:'capitalize'}}>{e.primaryMuscle}</div>}
                      </div>
                    </button>
                    {hi<hits.length-1&&<div style={{height:'0.5px',background:C.fill}}/>}
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

    </div>
  );
});

// ─── IMPORT FORM ──────────────────────────────────────────────────────────────
export function ImportForm({db,onSave}) {
  const [raw,setRaw]=useState("");
  const [preview,setPreview]=useState(null);
  const [scheda,setScheda]=useState("");
  const [error,setError]=useState("");
  const [saved,setSaved]=useState(false);
  const analyze=()=>{
    setError("");setPreview(null);
    const p=parseHeavyNote(raw);
    if(!p){setError("Formato non valido. Incolla il testo esportato da Hevy (Profilo → Impostazioni → Esporta dati).");return;}
    setPreview(p); if(p.workoutName)setScheda(p.workoutName);
  };
  const confirm=()=>{
    if(!preview)return;
    const session={id:Date.now(),date:preview.date,workoutName:scheda||preview.workoutName||"",exercises:preview.exercises};
    onSave({...db,sessions:[session,...db.sessions]}); setSaved(true);
    setTimeout(()=>{setSaved(false);setPreview(null);setRaw("");setScheda("");},900);
  };
  return (
    <div>
      <div style={{background:C.bg,borderRadius:10,padding:"10px 12px",marginBottom:10,fontSize:12,color:C.subtle,fontFamily:FONT_MONO,lineHeight:1.7,border:`1px dashed ${C.border}`}}>
        Scheda: Gambe e Spalle<br/>Il giorno martedi, apr 07, 2026 alle ore 4:36pm<br/>Lento in Avanti (Smith Machine)<br/>Serie 1: 10 kg x 10 @ 8 rpe
      </div>
      <textarea value={raw} onChange={e=>{setRaw(e.target.value);setPreview(null);setSaved(false);}}
        placeholder="Incolla qui le note da Hevy..."
        style={{...St.input,minHeight:110,resize:"vertical",lineHeight:1.6,fontSize:13,fontFamily:FONT_MONO}}/>
      {error&&<div style={{marginTop:6,fontSize:13,color:C.red}}>{error}</div>}
      <button onClick={analyze} style={{...St.btn(),marginTop:10,width:"100%"}}>Analizza</button>
      {preview&&(<div style={{marginTop:14}}>
        <div style={{background:C.blueL,border:`1px solid ${C.blueB}`,borderRadius:10,padding:"12px 14px",marginBottom:12}}>
          <div style={{fontSize:12,color:C.muted}}>{new Date(preview.date).toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
          <div style={{fontWeight:700,fontSize:15,color:C.text,marginTop:3}}>{preview.workoutName||"Sessione importata"}</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>{preview.exercises.length} esercizi rilevati</div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={{...St.label,marginBottom:8,display:"block"}}>Assegna a scheda</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
            {(()=>{
              const activeSchede=(db.schede||[]).filter(s=>s.active!==false);
              const names=activeSchede.length?activeSchede.map(s=>s.name):[...new Set(db.sessions.map(s=>s.workoutName).filter(Boolean))];
              return[{label:"Nessuna",val:""}, ...names.map(n=>({label:n,val:n}))].map(({label,val})=>(
                <button key={val} onClick={()=>setScheda(val)}
                  style={{padding:"8px 14px",borderRadius:20,border:`1.5px solid ${scheda===val?C.blue:C.border}`,
                    background:scheda===val?C.blueL:"transparent",
                    color:scheda===val?C.blue:C.muted,
                    fontSize:13,fontWeight:scheda===val?600:400,
                    cursor:"pointer",fontFamily:FONT_BODY,transition:"all 0.15s"}}>
                  {label}
                </button>
              ));
            })()}
          </div>
        </div>
        <SessionStats exercises={preview.exercises} allSessions={db.sessions} date={preview.date}/>
        {preview.exercises.map((ex,i)=>{
          const prevSess=db.sessions.find(s=>s.exercises.some(e=>e.name.toLowerCase().trim()===ex.name.toLowerCase().trim()));
          const prevEx=prevSess?.exercises.find(e=>e.name.toLowerCase().trim()===ex.name.toLowerCase().trim());
          return <ExercisePreview key={i} ex={ex} prevEx={prevEx} allSessions={db.sessions} showInsights={true}/>;
        })}
        <button onClick={confirm} style={{...St.btn(saved?C.green:C.blue),width:"100%",padding:"13px",borderRadius:12,fontSize:15,marginBottom:8,marginTop:12}}>
          {saved?"Salvato":"Salva in cronologia"}
        </button>
        <button onClick={()=>setPreview(null)} style={{...St.btn(C.muted,true),width:"100%",padding:"11px",borderRadius:12,fontSize:14}}>
          Annulla
        </button>
      </div>)}
    </div>
  );
}

// ─── CSV IMPORT FORM ─────────────────────────────────────────────────────────
export function CsvImportForm({db,onSave}) {
  const [raw,setRaw]=useState("");
  const [parsed,setParsed]=useState(null);
  const [done,setDone]=useState(false);
  const [error,setError]=useState("");

  const analyze=()=>{
    setError("");setParsed(null);setDone(false);
    const sessions=parseHevyCSV(raw);
    if(!sessions){setError("Formato non valido. Incolla il CSV esportato da Hevy (Profilo → Impostazioni → Esporta dati).");return;}
    setParsed(sessions);
  };

  const existingKeys=new Set(db.sessions.map(s=>`${s.date}|||${s.workoutName}`));
  const toImport=parsed?parsed.filter(s=>`${s.date}|||${s.workoutName}`&&!existingKeys.has(`${s.date}|||${s.workoutName}`)):[];
  const already=parsed?parsed.length-toImport.length:0;

  const doImport=async()=>{
    if(!toImport.length) return;
    const normalized=toImport.map(s=>{
      const match=(db.schede||[]).find(sc=>sessionFitsScheda(s.workoutName,sc.name));
      return match?{...s,workoutName:match.name}:s;
    });
    const merged=[...normalized,...db.sessions].sort((a,b)=>b.date.localeCompare(a.date));
    setError("");
    try {
      await onSave({...db,sessions:merged});
      setDone(true);
      setTimeout(()=>{setDone(false);setParsed(null);setRaw("");},1800);
    } catch {
      setError("Errore durante il salvataggio. Controlla la connessione e riprova.");
    }
  };

  return (
    <div>
      <div style={{background:C.bg,borderRadius:10,padding:"10px 12px",marginBottom:10,fontSize:12,color:C.subtle,lineHeight:1.7,border:`1px dashed ${C.border}`}}>
        Esporta il CSV da Hevy: <strong>Profilo → Impostazioni → Esporta dati</strong>. Poi incolla il contenuto del file qui sotto.
      </div>
      <textarea value={raw} onChange={e=>{setRaw(e.target.value);setParsed(null);setDone(false);}}
        placeholder={`"title","start_time","end_time","description","exercise_title",...`}
        style={{...St.input,minHeight:120,resize:"vertical",lineHeight:1.5,fontSize:12,fontFamily:FONT_MONO}}/>
      {error&&<div style={{marginTop:6,fontSize:12,color:C.red,lineHeight:1.5}}>{error}</div>}
      <button onClick={analyze} style={{...St.btn(),marginTop:10,width:"100%"}}>Analizza CSV</button>

      {parsed&&(
        <div style={{marginTop:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
            {[
              {label:"Sessioni nel file", val:parsed.length, color:C.text},
              {label:"Già presenti",      val:already,       color:C.muted},
              {label:"Da importare",      val:toImport.length, color:toImport.length>0?C.green:C.subtle},
            ].map(s=>(
              <div key={s.label} style={{background:C.bg,borderRadius:10,padding:"10px 8px",textAlign:"center"}}>
                <div style={{fontFamily:FONT_MONO,fontSize:20,fontWeight:600,color:s.color}}>{s.val}</div>
                <div style={{fontSize:10,color:C.subtle,marginTop:3}}>{s.label}</div>
              </div>
            ))}
          </div>
          {toImport.length>0&&(
            <>
              <div style={{fontSize:10,fontWeight:700,color:C.subtle,letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>Sessioni nuove</div>
              <div style={{maxHeight:260,overflowY:"auto",marginBottom:14,display:"flex",flexDirection:"column",gap:6}}>
                {toImport.map((s,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.greenL,border:`1px solid ${C.greenB}`,borderRadius:9,padding:"8px 12px"}}>
                    <div>
                      <div style={{fontWeight:600,fontSize:13,color:C.text}}>{s.workoutName}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:1}}>
                        {new Date(s.date).toLocaleDateString("it-IT",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}
                        {" · "}{s.exercises.length} esercizi
                      </div>
                    </div>
                    <span style={{...St.tag(C.green,C.greenL),fontSize:10}}>nuova</span>
                  </div>
                ))}
              </div>
              <button onClick={doImport} style={{...St.btn(done?C.green:C.blue),width:"100%",padding:"13px",borderRadius:12,fontSize:15,marginBottom:8}}>
                {done?`${toImport.length} sessioni importate`:`Importa ${toImport.length} sessioni`}
              </button>
            </>
          )}
          {toImport.length===0&&<div style={{textAlign:"center",padding:"14px 0",fontSize:13,color:C.muted}}>Tutte le sessioni sono già presenti nel database.</div>}
          <button onClick={()=>{setParsed(null);}} style={{...St.btn(C.muted,true),width:"100%",padding:"11px",borderRadius:12,fontSize:14}}>Annulla</button>
        </div>
      )}
    </div>
  );
}
